import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr } from "../../api";
import { Upload, Loader2, X, Trash2, CheckCircle2, AlertTriangle, Info, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const inputCls = "bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark transition-colors text-ink text-sm";
const fmt = (v) => `RM ${Number(v || 0).toFixed(2)}`;

const CATEGORY_STYLES = {
  TNG:        { bg: "bg-blue-50",      text: "text-blue-800",      dot: "bg-blue-500" },
  Card:       { bg: "bg-purple-50",    text: "text-purple-800",    dot: "bg-purple-500" },
  DuitNow:    { bg: "bg-emerald-50",   text: "text-emerald-800",   dot: "bg-emerald-500" },
  GrabPay:    { bg: "bg-green-50",     text: "text-green-800",     dot: "bg-green-500" },
  GrabFood:   { bg: "bg-green-50",     text: "text-green-800",     dot: "bg-green-600" },
  Foodpanda:  { bg: "bg-pink-50",      text: "text-pink-800",      dot: "bg-pink-500" },
  ShopeeFood: { bg: "bg-orange-50",    text: "text-orange-800",    dot: "bg-orange-500" },
  IBG:        { bg: "bg-slate-100",    text: "text-slate-800",     dot: "bg-slate-500" },
  Cash:       { bg: "bg-yellow-50",    text: "text-yellow-800",    dot: "bg-yellow-500" },
  Other:      { bg: "bg-ink/5",        text: "text-ink",           dot: "bg-ink/40" },
};

function CategoryBadge({ cat, className = "" }) {
  const s = CATEGORY_STYLES[cat] || CATEGORY_STYLES.Other;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.14em] ${s.bg} ${s.text} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {cat}
    </span>
  );
}

// ---------------- Main Bank Recon tab ----------------
export default function BankRecon() {
  const [statements, setStatements] = useState([]);
  const [openStatementId, setOpenStatementId] = useState(null);
  const [openStatement, setOpenStatement] = useState(null);
  const [parse, setParse] = useState(null);           // parse preview (pre-commit)
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState("");
  const [reconMonth, setReconMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [settlement, setSettlement] = useState("smart"); // smart | raw
  const [breakdown, setBreakdown] = useState(null);
  // Bumped when GEBME Order History is (re)imported so downstream sections refresh.
  const [ohBump, setOhBump] = useState(0);
  const fileRef = useRef(null);

  const loadStatements = async () => {
    try {
      const { data } = await api.get("/mgmt/bank/statements");
      setStatements(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  const loadRecon = async () => {
    if (!/^\d{4}-\d{2}$/.test(reconMonth)) return;
    setReconLoading(true);
    try {
      const [{ data: reconData }, { data: bd }] = await Promise.all([
        api.get(`/mgmt/bank/reconcile?month=${reconMonth}&settlement=${settlement}`),
        api.get(`/mgmt/bank/breakdown?month=${reconMonth}`),
      ]);
      setRecon(reconData);
      setBreakdown(bd);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setReconLoading(false);
    }
  };
  useEffect(() => { loadStatements(); }, []);
  useEffect(() => { loadRecon(); }, [reconMonth, settlement]);

  const openDetail = async (sid) => {
    if (openStatementId === sid) {
      setOpenStatementId(null); setOpenStatement(null); return;
    }
    setOpenStatementId(sid); setOpenStatement(null);
    try {
      const { data } = await api.get(`/mgmt/bank/statements/${sid}`);
      setOpenStatement(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const uploadFile = async (f) => {
    setErr("");
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) { setErr("Please upload a PDF bank statement."); return; }
    if (f.size > 20 * 1024 * 1024) { setErr("File must be smaller than 20 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/mgmt/bank/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Attach a stable local index to each transaction (for edit tracking).
      data.transactions = (data.transactions || []).map((t, i) => ({ ...t, _idx: i }));
      setParse(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    if (!parse) return;
    setCommitting(true); setErr("");
    try {
      const body = {
        bank: parse.bank,
        account_no: parse.account_no || "",
        statement_date: parse.statement_date || "",
        period_start: parse.period_start,
        period_end: parse.period_end,
        opening_balance: parse.opening_balance,
        closing_balance: parse.closing_balance,
        filename: parse.filename || "",
        transactions: parse.transactions.map((t) => ({
          date: t.date, description: t.description, amount: t.amount,
          direction: t.direction, balance: t.balance,
          category: t.category, excluded: !!t.excluded, note: t.note || "",
        })),
      };
      await api.post("/mgmt/bank/statements", body);
      setParse(null);
      await loadStatements();
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const removeStatement = async (sid) => {
    if (!window.confirm("Delete this bank statement? All its transactions will be removed from reconciliation.")) return;
    try {
      await api.delete(`/mgmt/bank/statements/${sid}`);
      if (openStatementId === sid) { setOpenStatementId(null); setOpenStatement(null); }
      await loadStatements();
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const patchTxn = async (sid, txnIdx, patch) => {
    // Optimistic update
    setOpenStatement((s) => s && ({
      ...s,
      transactions: s.transactions.map((t, i) => i === txnIdx ? { ...t, ...patch } : t),
    }));
    try {
      await api.patch(`/mgmt/bank/statements/${sid}/transactions/${txnIdx}`, patch);
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
      // Reload on failure to resync
      const { data } = await api.get(`/mgmt/bank/statements/${sid}`);
      setOpenStatement(data);
    }
  };

  const reclassifyStatement = async (sid) => {
    if (!window.confirm("Re-run the auto-classifier on every credit in this statement? Any manual category changes will be overwritten.")) return;
    try {
      const { data } = await api.post(`/mgmt/bank/statements/${sid}/reclassify`);
      window.alert(`Reclassified ${data.changed} of ${data.total_credits} credits.`);
      // Refresh open detail + recon
      const { data: stmt } = await api.get(`/mgmt/bank/statements/${sid}`);
      setOpenStatement(stmt);
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="bank-recon">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">Section 1 · Bank Reconciliation</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            CIMB eStatement · Match daily payments received to POS revenue
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Upload your <strong className="text-ink">CIMB bank statement PDF</strong>. We auto-classify each credit
            (Touch &apos;n Go · Credit Card · DuitNow QR · Foodpanda · GrabPay · IBG) and reconcile them
            <strong className="text-ink"> day-by-day against your POS revenue</strong>, applying T+1 settlement shifts
            so bank credits align with the sales day they represent.
          </p>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid="bank-err">
          {err}
        </p>
      )}

      {/* Upload Zone */}
      {!parse && (
        <div
          data-testid="bank-drop-zone"
          className="border-2 border-dashed border-ink/20 rounded-sm p-10 text-center bg-cream/40 hover:bg-cream/70 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) uploadFile(f);
          }}
        >
          <input
            ref={fileRef}
            data-testid="bank-file-input"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <Upload className="mx-auto text-ink-soft" size={36} />
          <p className="mt-4 text-ink">
            <strong>Drop your CIMB eStatement PDF here</strong> or click to browse
          </p>
          <p className="text-ink-soft text-xs mt-1">PDF only · up to 20 MB · 100% offline (no image scanning)</p>
          {uploading && (
            <p className="mt-3 inline-flex items-center gap-2 text-matcha-dark">
              <Loader2 className="animate-spin" size={16} />
              Parsing statement…
            </p>
          )}
        </div>
      )}

      {/* Parse Preview */}
      {parse && (
        <ParsePreview
          parse={parse}
          setParse={setParse}
          committing={committing}
          onCommit={commit}
          onCancel={() => setParse(null)}
        />
      )}

      {/* Saved statements list */}
      <SavedStatements
        statements={statements}
        openId={openStatementId}
        openDetail={openDetail}
        openStatement={openStatement}
        onDelete={removeStatement}
        onPatchTxn={patchTxn}
        onReclassify={reclassifyStatement}
      />

      {/* Historical uploads — click any past month to jump straight to its
          full recon (all 4 sections re-fetch at once). Every upload is
          persisted in Mongo, so months are revisitable indefinitely. */}
      <HistoricalUploadsStrip month={reconMonth} setMonth={setReconMonth} />

      {/* Actual Payments Received — collapsible breakdown by channel */}
      <PaymentsBreakdown
        breakdown={breakdown}
        month={reconMonth}
        setMonth={setReconMonth}
        loading={reconLoading}
      />

      {/* Reconciliation table */}
      <ReconTable
        recon={recon}
        loading={reconLoading}
        month={reconMonth}
        setMonth={setReconMonth}
        settlement={settlement}
        setSettlement={setSettlement}
      />

      {/* Credit-card Merchant Statement — separate PDF from the acquirer
          (Public Bank) so we can pull the ACTUAL MDR% instead of estimating
          Card fees. Persists per-month; recon joins with POS Card revenue. */}
      <MerchantStatementSection month={reconMonth} setMonth={setReconMonth} />

      {/* GEBME Order History — SOURCE OF TRUTH. Every downstream matcher
          (Foodpanda invoice-level, Grab settlement-level, CIMB day match)
          joins against pos_transactions imported here. Independent section
          so admins never have to hunt for the drop zone. */}
      <OrderHistorySection
        month={reconMonth}
        setMonth={setReconMonth}
        onImported={() => setOhBump((b) => b + 1)}
      />

      {/* Foodpanda Payment Advice — one PDF per bank transfer. Reconciles
          POS Foodpanda channel gross ↔ invoice net ↔ CIMB bank credits. */}
      <FoodpandaPayoutSection month={reconMonth} setMonth={setReconMonth} ohBump={ohBump} />

      {/* Grab Transfers — one CSV per period. Reconciles POS Grab channel
          gross (grabfood + grabpay) ↔ settlement net ↔ CIMB bank credits. */}
      <GrabTransferSection month={reconMonth} setMonth={setReconMonth} ohBump={ohBump} />

      {/* Touch 'n Go Daily Settlement — one MDSDO_P_YYYYMMDD xlsx per
          settlement day. Reconciles POS TNG channel gross ↔ TNG settlement
          net ↔ CIMB AUTOPAY CR credits from UTMB TNG DIGITAL. */}
      <TngSettlementSection month={reconMonth} setMonth={setReconMonth} ohBump={ohBump} />
    </div>
  );
}

// ---------------- Parse Preview ----------------
function ParsePreview({ parse, setParse, committing, onCommit, onCancel }) {
  const credits = parse.transactions.filter((t) => t.direction === "credit");
  const debits  = parse.transactions.filter((t) => t.direction === "debit");
  const totalCredits = credits.reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalDebits  = debits.reduce((s, t) => s + Number(t.amount || 0), 0);

  const byCategory = useMemo(() => {
    const m = {};
    for (const t of credits) {
      const c = t.category || "Other";
      if (!m[c]) m[c] = { count: 0, amount: 0 };
      m[c].count += 1;
      m[c].amount += Number(t.amount || 0);
    }
    return Object.entries(m).sort((a, b) => b[1].amount - a[1].amount);
  }, [credits]);

  const setTxnField = (idx, patch) => {
    setParse((p) => ({
      ...p,
      transactions: p.transactions.map((t) => t._idx === idx ? { ...t, ...patch } : t),
    }));
  };

  return (
    <div className="bg-bone border border-ink/10 rounded-sm p-6 space-y-6" data-testid="bank-preview">
      {/* Summary */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-ink/10">
        <div>
          <p className="overline text-matcha-dark">Preview</p>
          <p className="font-display text-xl text-ink">
            {parse.bank} · {parse.account_no || "—"}
          </p>
          <p className="text-ink-soft text-xs mt-1">
            {parse.filename} · {parse.period_start} → {parse.period_end} · Opening {fmt(parse.opening_balance)} → Closing {fmt(parse.closing_balance)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="bank-preview-cancel"
            onClick={onCancel}
            className="px-4 py-2 border border-ink/15 rounded-sm text-ink hover:bg-cream text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="bank-preview-commit"
            disabled={committing}
            onClick={onCommit}
            className="px-5 py-2 bg-matcha-dark text-white rounded-sm text-sm hover:bg-matcha transition-colors disabled:opacity-50"
          >
            {committing ? "Saving…" : `Save Statement · ${parse.transactions.length} txns`}
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Credits (Deposits)" value={fmt(totalCredits)} sub={`${credits.length} transactions`} tone="pos" />
        <Stat label="Debits (Withdrawals)" value={fmt(totalDebits)} sub={`${debits.length} transactions`} tone="neg" />
        <Stat label="Net Change" value={fmt(totalCredits - totalDebits)} sub="Deposits − Withdrawals" />
        <Stat label="Balance Δ" value={fmt((parse.closing_balance || 0) - (parse.opening_balance || 0))} sub="Closing − Opening" />
      </div>

      {/* Category breakdown */}
      <div>
        <p className="overline text-ink mb-3">Credit Category Breakdown</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {byCategory.map(([cat, { count, amount }]) => (
            <div key={cat} className="border border-ink/10 rounded-sm p-3">
              <CategoryBadge cat={cat} />
              <p className="text-ink text-lg mt-2">{fmt(amount)}</p>
              <p className="text-ink-soft text-[11px]">{count} credit{count === 1 ? "" : "s"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Transactions table */}
      <div>
        <p className="overline text-ink mb-3">Transactions · edit category / exclude before saving</p>
        <div className="border border-ink/10 rounded-sm overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-sage/30 text-left text-ink-soft sticky top-0">
              <tr>
                <th className="px-3 py-2 overline">Date</th>
                <th className="px-3 py-2 overline">Description</th>
                <th className="px-3 py-2 overline text-right">Amount</th>
                <th className="px-3 py-2 overline">Dir</th>
                <th className="px-3 py-2 overline">Category</th>
                <th className="px-3 py-2 overline text-center">Exclude</th>
              </tr>
            </thead>
            <tbody>
              {parse.transactions.map((t) => (
                <tr key={t._idx} data-testid={`bank-txn-row-${t._idx}`} className={`border-t border-ink/5 ${t.excluded ? "opacity-40" : ""}`}>
                  <td className="px-3 py-2 text-ink whitespace-nowrap">{t.date}</td>
                  <td className="px-3 py-2 text-ink-soft text-xs max-w-md truncate" title={t.description}>{t.description}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${t.direction === "credit" ? "text-emerald-700" : "text-red-700"}`}>
                    {t.direction === "credit" ? "+" : "−"} {fmt(t.amount)}
                  </td>
                  <td className="px-3 py-2 text-ink-soft text-xs uppercase tracking-wider">{t.direction}</td>
                  <td className="px-3 py-2">
                    {t.direction === "credit" ? (
                      <select
                        data-testid={`bank-txn-cat-${t._idx}`}
                        value={t.category || "Other"}
                        onChange={(e) => setTxnField(t._idx, { category: e.target.value })}
                        className={inputCls + " text-xs py-1"}
                      >
                        {["TNG", "Card", "DuitNow", "GrabPay", "GrabFood", "Foodpanda", "ShopeeFood", "IBG", "Cash", "Other"].map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-ink-soft text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      data-testid={`bank-txn-excl-${t._idx}`}
                      checked={!!t.excluded}
                      onChange={(e) => setTxnField(t._idx, { excluded: e.target.checked })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Saved Statements ----------------
function SavedStatements({ statements, openId, openDetail, openStatement, onDelete, onPatchTxn, onReclassify }) {
  if (!statements.length) {
    return (
      <div className="border border-dashed border-ink/15 rounded-sm p-6 text-center text-ink-soft text-sm" data-testid="bank-empty">
        <Info className="mx-auto mb-2" size={20} />
        No bank statements imported yet. Upload one above to start reconciling.
      </div>
    );
  }
  return (
    <div>
      <p className="overline text-ink mb-3">Saved Bank Statements ({statements.length})</p>
      <div className="border border-ink/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sage/40 text-left text-ink-soft">
            <tr>
              <th className="px-3 py-2 overline"></th>
              <th className="px-3 py-2 overline">Bank</th>
              <th className="px-3 py-2 overline">Account</th>
              <th className="px-3 py-2 overline">Period</th>
              <th className="px-3 py-2 overline text-right">Credits</th>
              <th className="px-3 py-2 overline text-right">Debits</th>
              <th className="px-3 py-2 overline text-right">Opening → Closing</th>
              <th className="px-3 py-2 overline text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <React.Fragment key={s.id}>
                <tr className="border-t border-ink/5" data-testid={`bank-stmt-row-${s.id}`}>
                  <td className="px-3 py-2">
                    <button
                      data-testid={`bank-stmt-toggle-${s.id}`}
                      onClick={() => openDetail(s.id)}
                      className="text-ink-soft hover:text-ink"
                    >
                      {openId === s.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-ink">{s.bank}</td>
                  <td className="px-3 py-2 text-ink-soft font-mono text-xs">{s.account_no}</td>
                  <td className="px-3 py-2 text-ink-soft">{s.period_start} → {s.period_end}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 whitespace-nowrap">{fmt(s.credit_total)} <span className="text-ink-soft">({s.credit_count})</span></td>
                  <td className="px-3 py-2 text-right text-red-700 whitespace-nowrap">{fmt(s.debit_total)} <span className="text-ink-soft">({s.debit_count})</span></td>
                  <td className="px-3 py-2 text-right text-ink whitespace-nowrap">{fmt(s.opening_balance)} → {fmt(s.closing_balance)}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        data-testid={`bank-stmt-reclass-${s.id}`}
                        onClick={() => onReclassify(s.id)}
                        className="text-ink-soft hover:text-matcha-dark"
                        title="Re-run auto-classifier"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        data-testid={`bank-stmt-del-${s.id}`}
                        onClick={() => onDelete(s.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete statement"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                {openId === s.id && openStatement && (
                  <tr>
                    <td colSpan={8} className="p-0 bg-cream/40">
                      <StatementDetail
                        stmt={openStatement}
                        onPatchTxn={(idx, patch) => onPatchTxn(s.id, idx, patch)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatementDetail({ stmt, onPatchTxn }) {
  return (
    <div className="p-4 border-t border-ink/10">
      <div className="border border-ink/10 rounded-sm overflow-x-auto max-h-[400px] overflow-y-auto bg-bone">
        <table className="w-full text-sm">
          <thead className="bg-sage/20 text-left text-ink-soft sticky top-0">
            <tr>
              <th className="px-3 py-2 overline">Date</th>
              <th className="px-3 py-2 overline">Description</th>
              <th className="px-3 py-2 overline text-right">Amount</th>
              <th className="px-3 py-2 overline">Category</th>
              <th className="px-3 py-2 overline text-center">Excluded</th>
            </tr>
          </thead>
          <tbody>
            {(stmt.transactions || []).map((t, i) => (
              <tr key={i} data-testid={`stmt-txn-${i}`} className={`border-t border-ink/5 ${t.excluded ? "opacity-40" : ""}`}>
                <td className="px-3 py-2 text-ink whitespace-nowrap">{t.date}</td>
                <td className="px-3 py-2 text-ink-soft text-xs max-w-lg truncate" title={t.description}>{t.description}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${t.direction === "credit" ? "text-emerald-700" : "text-red-700"}`}>
                  {t.direction === "credit" ? "+" : "−"} {fmt(t.amount)}
                </td>
                <td className="px-3 py-2">
                  {t.direction === "credit" ? (
                    <select
                      data-testid={`stmt-txn-cat-${i}`}
                      value={t.category || "Other"}
                      onChange={(e) => onPatchTxn(i, { category: e.target.value })}
                      className={inputCls + " text-xs py-1"}
                    >
                      {["TNG", "Card", "DuitNow", "GrabPay", "GrabFood", "Foodpanda", "ShopeeFood", "IBG", "Cash", "Other"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-ink-soft text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {t.direction === "credit" && (
                    <input
                      type="checkbox"
                      data-testid={`stmt-txn-excl-${i}`}
                      checked={!!t.excluded}
                      onChange={(e) => onPatchTxn(i, { excluded: e.target.checked })}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Payments Breakdown (collapsible per channel) ----------------
const GROUP_META = {
  Grab:       { label: "Grab (GrabPay + GrabFood)",   accent: "bg-green-50 border-green-200",   dot: "bg-green-500",   note: "Settlements from GPAY NETWORK (M) SDN — includes both GrabPay QR & GrabFood aggregator" },
  TNG:        { label: "Touch 'n Go (TNG)",            accent: "bg-blue-50 border-blue-200",     dot: "bg-blue-500",    note: "AUTOPAY CR settlements from UTMB TNG DIGITAL — T+1 typical" },
  IBG:        { label: "IBG (Interbank Transfers In)", accent: "bg-slate-50 border-slate-200",   dot: "bg-slate-500",   note: "Incoming interbank credits — often walk-in bank transfers or vendor refunds" },
  Credit:     { label: "Credit / Debit Card",          accent: "bg-purple-50 border-purple-200", dot: "bg-purple-500",  note: "Merchant acquirer settlements — non-TNG AUTOPAY CR entries" },
  Cash:       { label: "Cash Takings (Yong Chin Wen)", accent: "bg-yellow-50 border-yellow-200", dot: "bg-yellow-500",  note: "Daily cash banking via DuitNow from Yong Chin Wen — tracks physical cash received at outlet" },
  Foodpanda:  { label: "Foodpanda",                    accent: "bg-pink-50 border-pink-200",     dot: "bg-pink-500",    note: "Settlements from DELIVERY HERO MALAYSIA" },
  Shopee:     { label: "ShopeeFood",                   accent: "bg-orange-50 border-orange-200", dot: "bg-orange-500",  note: "Settlements from SHOPEE" },
  DuitNow:    { label: "DuitNow (Other)",              accent: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500", note: "Direct customer DuitNow QR transfers not attributable to any aggregator" },
  Other:      { label: "Other / Unclassified",         accent: "bg-ink/5 border-ink/15",         dot: "bg-ink/40",      note: "Manually reclassify from the statement drawer if needed" },
};

function PaymentsBreakdown({ breakdown, month, setMonth, loading }) {
  const [open, setOpen] = useState({}); // group name → bool

  const toggle = (g) => setOpen((o) => ({ ...o, [g]: !o[g] }));

  return (
    <div className="bg-bone border border-ink/10 rounded-sm p-6" data-testid="bank-breakdown">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="overline text-matcha-dark">Actual Payments Received</p>
          <h3 className="font-display text-xl text-ink">Breakdown by channel — {month}</h3>
          <p className="text-ink-soft text-sm mt-1">Tap any row to expand and see every individual bank credit.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-ink-soft uppercase tracking-[0.16em]">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls + " text-sm"}
            data-testid="bank-breakdown-month"
          />
        </div>
      </div>

      {loading && (
        <p className="text-ink-soft text-sm inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</p>
      )}

      {!loading && breakdown && (
        <>
          {/* Grand total ribbon */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            <Stat label="Total Received" value={fmt(breakdown.grand_total)} sub={`${breakdown.grand_count} credits across all channels`} />
            <Stat label="Channels Active" value={String((breakdown.groups || []).length)} sub={`For ${month}`} />
            <Stat
              label="Largest Channel"
              value={(breakdown.groups && breakdown.groups[0]) ? GROUP_META[breakdown.groups[0].group]?.label?.split(" ")[0] || breakdown.groups[0].group : "—"}
              sub={(breakdown.groups && breakdown.groups[0]) ? fmt(breakdown.groups.reduce((m, g) => g.total > m.total ? g : m, breakdown.groups[0]).total) : ""}
            />
          </div>

          {(breakdown.groups || []).length === 0 && (
            <p className="text-ink-soft text-sm text-center py-8">No bank credits imported for {month}.</p>
          )}

          <div className="space-y-3">
            {(breakdown.groups || []).map((g) => {
              const meta = GROUP_META[g.group] || GROUP_META.Other;
              const isOpen = !!open[g.group];
              return (
                <div key={g.group} className={`border rounded-sm ${meta.accent}`} data-testid={`breakdown-group-${g.group}`}>
                  {/* Header row (clickable) */}
                  <button
                    type="button"
                    data-testid={`breakdown-toggle-${g.group}`}
                    onClick={() => toggle(g.group)}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-ink/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? <ChevronDown size={16} className="text-ink-soft flex-shrink-0" /> : <ChevronRight size={16} className="text-ink-soft flex-shrink-0" />}
                      <span className={`w-2 h-2 rounded-full ${meta.dot} flex-shrink-0`} />
                      <div className="min-w-0">
                        <p className="text-ink text-base leading-tight truncate">{meta.label}</p>
                        <p className="text-ink-soft text-[11px] mt-0.5 truncate">{meta.note}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-ink text-lg" data-testid={`breakdown-total-${g.group}`}>{fmt(g.total)}</p>
                      <p className="text-ink-soft text-[11px]">{g.count} transaction{g.count === 1 ? "" : "s"}</p>
                    </div>
                  </button>

                  {/* Expanded transaction list */}
                  {isOpen && (
                    <div className="border-t border-ink/10 bg-white/60">
                      <div className="max-h-[360px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-white/80 backdrop-blur-sm text-left text-ink-soft border-b border-ink/10">
                            <tr>
                              <th className="px-4 py-2 overline">Date</th>
                              <th className="px-4 py-2 overline">Description</th>
                              <th className="px-4 py-2 overline">Sub-cat</th>
                              <th className="px-4 py-2 overline text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.transactions.map((t, i) => (
                              <tr key={`${t.statement_id}-${t.txn_idx}`} data-testid={`breakdown-txn-${g.group}-${i}`} className="border-t border-ink/5 hover:bg-ink/[0.02]">
                                <td className="px-4 py-2 text-ink whitespace-nowrap">{t.date}</td>
                                <td className="px-4 py-2 text-ink-soft max-w-md truncate" title={t.description}>{t.description}</td>
                                <td className="px-4 py-2"><CategoryBadge cat={t.category} /></td>
                                <td className="px-4 py-2 text-right text-emerald-700 whitespace-nowrap">{fmt(t.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-ink/20 bg-white/80">
                              <td colSpan={3} className="px-4 py-2 text-right text-ink uppercase tracking-wider text-[11px]">Subtotal</td>
                              <td className="px-4 py-2 text-right text-ink">{fmt(g.total)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Recon Table ----------------
function ReconTable({ recon, loading, month, setMonth, settlement, setSettlement }) {
  const activeCats = useMemo(() => {
    if (!recon) return [];
    // Show categories where either bank or POS has any activity for the month
    return (recon.categories || []).filter((c) => {
      const p = recon.totals?.pos?.[c] || 0;
      const b = recon.totals?.bank?.[c] || 0;
      return p !== 0 || b !== 0;
    });
  }, [recon]);

  return (
    <div className="bg-bone border border-ink/10 rounded-sm p-6" data-testid="bank-recon-table">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="overline text-matcha-dark">Reconciliation</p>
          <h3 className="font-display text-xl text-ink">Bank vs POS — daily match</h3>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-ink-soft uppercase tracking-[0.16em]">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls + " text-sm"}
            data-testid="bank-recon-month"
          />
          <label className="text-xs text-ink-soft uppercase tracking-[0.16em] ml-4">Settle</label>
          <select
            value={settlement}
            onChange={(e) => setSettlement(e.target.value)}
            className={inputCls + " text-sm"}
            data-testid="bank-recon-settlement"
          >
            <option value="smart">Smart (T+1 for TNG/Card)</option>
            <option value="raw">Raw (T+0 same day)</option>
          </select>
        </div>
      </div>

      {loading && (
        <p className="text-ink-soft text-sm inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</p>
      )}
      {!loading && recon && (
        <>
          {/* Grand totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Stat label="POS Expected" value={fmt(recon.totals?.pos_grand || 0)} sub="Sum of sales for this month" />
            <Stat label="Bank Received" value={fmt(recon.totals?.bank_grand || 0)} sub={`${settlement === "smart" ? "T+N settled" : "Same-day"} into ${month}`} />
            <Stat
              label="Variance"
              value={fmt(recon.totals?.variance_grand || 0)}
              sub={Math.abs(recon.totals?.variance_grand || 0) < 0.5 ? "In balance ✓" : (recon.totals?.variance_grand > 0 ? "Bank > POS" : "POS > Bank")}
              tone={Math.abs(recon.totals?.variance_grand || 0) < 0.5 ? "pos" : "neg"}
            />
          </div>

          {/* Per-category totals */}
          <div className="mb-6">
            <p className="overline text-ink mb-3">By Category</p>
            <div className="border border-ink/10 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sage/40 text-left text-ink-soft">
                  <tr>
                    <th className="px-3 py-2 overline">Category</th>
                    <th className="px-3 py-2 overline text-right">POS</th>
                    <th className="px-3 py-2 overline text-right">Bank</th>
                    <th className="px-3 py-2 overline text-right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCats.map((c) => {
                    const p = recon.totals?.pos?.[c] || 0;
                    const b = recon.totals?.bank?.[c] || 0;
                    const v = b - p;
                    return (
                      <tr key={c} className="border-t border-ink/5" data-testid={`recon-cat-${c}`}>
                        <td className="px-3 py-2"><CategoryBadge cat={c} /></td>
                        <td className="px-3 py-2 text-right text-ink whitespace-nowrap">{fmt(p)}</td>
                        <td className="px-3 py-2 text-right text-ink whitespace-nowrap">{fmt(b)}</td>
                        <td className={`px-3 py-2 text-right whitespace-nowrap ${Math.abs(v) < 0.5 ? "text-emerald-700" : (v > 0 ? "text-blue-700" : "text-red-700")}`}>
                          {v >= 0 ? "+" : "−"}{fmt(Math.abs(v))}
                          {Math.abs(v) < 0.5 && <CheckCircle2 className="inline ml-1" size={12} />}
                        </td>
                      </tr>
                    );
                  })}
                  {activeCats.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-ink-soft text-sm">No data for {month} — upload a bank statement or import POS sales for this month.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily table */}
          <div>
            <p className="overline text-ink mb-3">Daily Match</p>
            <div className="border border-ink/10 rounded-sm overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-sage/40 text-left text-ink-soft sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 overline">Date</th>
                    <th className="px-3 py-2 overline text-right">POS</th>
                    <th className="px-3 py-2 overline text-right">Bank</th>
                    <th className="px-3 py-2 overline text-right">Variance</th>
                    {activeCats.map((c) => (
                      <th key={c} className="px-2 py-2 overline text-right whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(recon.days || []).map((d) => {
                    const hasActivity = d.pos_total > 0 || d.bank_total > 0;
                    if (!hasActivity) return null;
                    return (
                      <tr key={d.date} className="border-t border-ink/5" data-testid={`recon-day-${d.date}`}>
                        <td className="px-3 py-2 text-ink whitespace-nowrap">{d.date.slice(-2)}</td>
                        <td className="px-3 py-2 text-right text-ink whitespace-nowrap">{fmt(d.pos_total)}</td>
                        <td className="px-3 py-2 text-right text-ink whitespace-nowrap">{fmt(d.bank_total)}</td>
                        <td className={`px-3 py-2 text-right whitespace-nowrap ${Math.abs(d.variance) < 0.5 ? "text-emerald-700" : (d.variance > 0 ? "text-blue-700" : "text-red-700")}`}>
                          {d.variance >= 0 ? "+" : "−"}{fmt(Math.abs(d.variance))}
                        </td>
                        {activeCats.map((c) => {
                          const cell = d.categories?.[c] || { pos: 0, bank: 0, variance: 0 };
                          const bal = Math.abs(cell.variance) < 0.5 && (cell.pos !== 0 || cell.bank !== 0);
                          return (
                            <td
                              key={c}
                              className={`px-2 py-2 text-right whitespace-nowrap ${bal ? "text-emerald-700" : (cell.variance > 0 ? "text-blue-700" : cell.variance < 0 ? "text-red-700" : "text-ink-soft")}`}
                              title={`POS ${fmt(cell.pos)}  ·  Bank ${fmt(cell.bank)}`}
                            >
                              {cell.bank || cell.pos ? fmt(cell.bank || cell.pos) : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-ink-soft text-[11px] flex items-center gap-2">
              <AlertTriangle size={12} className="text-amber-600" />
              Rows shown only for days with POS or bank activity. Hover a cell to see POS vs Bank amounts.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Small components ----------------
function Stat({ label, value, sub, tone }) {
  const toneCls = tone === "pos" ? "text-emerald-700" : tone === "neg" ? "text-red-700" : "text-ink";
  return (
    <div className="border border-ink/10 rounded-sm bg-cream/40 p-3">
      <p className="overline text-ink-soft">{label}</p>
      <p className={`mt-1 text-lg ${toneCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-ink-soft mt-1">{sub}</p>}
    </div>
  );
}


// ---------------- Credit-card Merchant Statement (Public Bank) ----------------
// Distinct upload flow from the bank eStatement above. This is the monthly
// acquirer merchant statement which lists gross / MDR fee / net PER DAY
// (and per Merchant ID). We use it to:
//   • pull the ACTUAL MDR% instead of guessing
//   • see per-day variance vs POS Card revenue (i.e. GEBME cash-up)
function MerchantStatementSection({ month, setMonth }) {
  const [parse, setParse] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [recon, setRecon] = React.useState(null);
  const [reconLoading, setReconLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const fileRef = React.useRef(null);
  // Per-day merchant ↔ CIMB credit match state
  const [bankMatches, setBankMatches] = React.useState(null);
  const [bankMatchLoading, setBankMatchLoading] = React.useState(false);
  const [expandedStmtDate, setExpandedStmtDate] = React.useState(null);

  const loadRecon = React.useCallback(async () => {
    setReconLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/merchant-recon?month=${month}`);
      setRecon(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setReconLoading(false);
    }
  }, [month]);

  const loadBankMatches = React.useCallback(async () => {
    setBankMatchLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/merchant-bank-matches?month=${month}`);
      setBankMatches(data);
    } catch (e) {
      // Silent — a missing statement is expected when no PDF is uploaded yet.
    } finally {
      setBankMatchLoading(false);
    }
  }, [month]);

  React.useEffect(() => { loadRecon(); }, [loadRecon]);
  React.useEffect(() => { loadBankMatches(); }, [loadBankMatches]);

  const linkMerchantDay = async (stmt_date, txn_id) => {
    if (!txn_id) return;
    try {
      await api.post("/mgmt/bank/merchant-bank-link", {
        stmt_date, action: "link", txn_id,
      });
      await loadBankMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const markMerchantNoMatch = async (stmt_date) => {
    const note = window.prompt(`Mark ${stmt_date} as no-bank-match?\n\nReason (optional):`, "");
    if (note === null) return;
    try {
      await api.post("/mgmt/bank/merchant-bank-link", {
        stmt_date, action: "no_match", note: note || "",
      });
      await loadBankMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const clearMerchantOverride = async (stmt_date) => {
    if (!window.confirm(`Clear the override on ${stmt_date}?`)) return;
    try {
      await api.post("/mgmt/bank/merchant-bank-link", {
        stmt_date, action: "clear",
      });
      await loadBankMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const uploadFile = async (f) => {
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/mgmt/bank/parse-merchant-stmt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParse(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!parse) return;
    // Two-step confirm — re-uploading REPLACES the existing statement for
    // this period, so we surface exactly what will happen before doing it.
    const monthKey = `${parse.period_year}-${String(parse.period_month).padStart(2, "0")}`;
    if (!window.confirm(
      `Save this Credit Card Merchant Statement?\n\n` +
      `Merchant: ${parse.merchant_name}\n` +
      `Period: ${monthKey}\n` +
      `Transactions: ${parse.totals?.txn_count || 0}\n` +
      `Gross: RM ${(parse.totals?.gross || 0).toFixed(2)}\n` +
      `MDR fee: RM ${(parse.totals?.disc || 0).toFixed(2)}\n` +
      `Effective MDR: ${(parse.totals?.mdr_pct || 0).toFixed(3)}%\n\n` +
      `Any existing statement for ${monthKey} will be replaced. ` +
      `The Card MDR line on the Dashboard will read from this statement.`
    )) return;
    setCommitting(true);
    setErr("");
    try {
      await api.post("/mgmt/bank/merchant-statements", {
        merchant_name: parse.merchant_name,
        deposit_acct: parse.deposit_acct,
        statement_date: parse.statement_date,
        period_year: parse.period_year,
        period_month: parse.period_month,
        mids: parse.mids,
        days: parse.days,
        totals: parse.totals,
        filename: parse.filename,
      });
      setParse(null);
      // Auto-jump the ReconTable month to the saved statement so both panels
      // stay in sync.
      if (parse.period_year && parse.period_month) {
        setMonth(`${parse.period_year}-${String(parse.period_month).padStart(2, "0")}`);
      }
      await loadRecon();
      await loadBankMatches();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const days = recon?.days || [];
  const totals = recon?.bank_totals || {};

  return (
    <section
      className="mt-10 rounded-sm border border-matcha-dark/25 bg-cream/40 p-6 space-y-4 shadow-sm"
      data-testid="merchant-stmt-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-ink/10 pb-4">
        <div>
          <p className="overline text-matcha-dark">Section 2 · Credit-card Merchant Statement</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Public Bank MDR fee report · authoritative Card commission
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Distinct from the CIMB eStatement above. Drop the acquirer&apos;s monthly
            <strong className="text-ink"> MERCHANT STATEMENT </strong>PDF here — we tabulate every day&apos;s
            gross / MDR fee / net across every Merchant ID, save it, and pipe the effective MDR%
            straight into the Dashboard <strong className="text-ink">Card MDR</strong> scorecard.
          </p>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid="merchant-stmt-err">
          {err}
        </p>
      )}

      {!parse && (
        <div
          data-testid="merchant-stmt-drop-zone"
          className="border-2 border-dashed border-ink/20 rounded-sm p-8 text-center bg-cream/40 hover:bg-cream/70 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) uploadFile(f);
          }}
        >
          <input
            ref={fileRef}
            data-testid="merchant-stmt-file-input"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <Upload className="mx-auto text-ink-soft" size={32} />
          <p className="mt-3 text-ink">
            <strong>Drop your merchant statement PDF here</strong> or click to browse
          </p>
          <p className="text-ink-soft text-xs mt-1">Public Bank format · up to 20 MB · 100% offline</p>
          {uploading && (
            <p className="mt-3 inline-flex items-center gap-2 text-matcha-dark">
              <Loader2 className="animate-spin" size={16} />
              Parsing statement…
            </p>
          )}
        </div>
      )}

      {parse && (
        <div className="rounded-sm border border-matcha-dark/30 bg-sage/10 p-4 space-y-4" data-testid="merchant-stmt-preview">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <p className="overline text-matcha-dark">Preview — click Save to persist</p>
              <p className="text-ink text-base">
                {parse.merchant_name} · Statement {parse.statement_date} · {parse.mids?.length || 0} MID{parse.mids?.length === 1 ? "" : "s"} · {parse.days?.length || 0} day{parse.days?.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="merchant-stmt-save"
                onClick={commit}
                disabled={committing}
                className="px-4 py-2 rounded-sm bg-matcha-dark text-white text-sm hover:bg-matcha disabled:opacity-50"
              >
                {committing ? "Saving…" : "Save statement"}
              </button>
              <button
                type="button"
                data-testid="merchant-stmt-cancel"
                onClick={() => setParse(null)}
                className="px-3 py-2 rounded-sm border border-ink/15 text-ink text-sm hover:bg-cream"
              >
                Cancel
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Transactions"     value={String(parse.totals?.txn_count || 0)} />
            <Stat label="Gross"            value={`RM ${(parse.totals?.gross || 0).toFixed(2)}`} />
            <Stat label="MDR fee"          value={`RM ${(parse.totals?.disc || 0).toFixed(2)}`} tone="neg" />
            <Stat label="Effective MDR"    value={`${(parse.totals?.mdr_pct || 0).toFixed(3)}%`} sub={`Net: RM ${(parse.totals?.net || 0).toFixed(2)}`} />
          </div>
          {(parse.mids || []).length > 1 && (
            <div className="text-xs">
              <p className="overline text-ink-soft mb-1">Per Merchant ID</p>
              <table className="w-full">
                <thead className="text-left text-ink-soft border-b border-ink/10">
                  <tr>
                    <th className="py-1.5 overline">MID</th>
                    <th className="py-1.5 overline text-right">Txns</th>
                    <th className="py-1.5 overline text-right">Gross</th>
                    <th className="py-1.5 overline text-right">MDR fee</th>
                    <th className="py-1.5 overline text-right">Net</th>
                    <th className="py-1.5 overline text-right">MDR%</th>
                  </tr>
                </thead>
                <tbody>
                  {parse.mids.map((m) => (
                    <tr key={m.mid} className="border-t border-ink/5">
                      <td className="py-1.5 text-ink font-mono">{m.mid}</td>
                      <td className="py-1.5 text-right">{m.txn_count}</td>
                      <td className="py-1.5 text-right">RM {m.gross.toFixed(2)}</td>
                      <td className="py-1.5 text-right text-red-700">RM {m.disc.toFixed(2)}</td>
                      <td className="py-1.5 text-right">RM {m.net.toFixed(2)}</td>
                      <td className="py-1.5 text-right">{m.mdr_pct.toFixed(3)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Recon table: bank days ⨝ POS card revenue per day */}
      {recon && (
        <div className="space-y-3">
          {recon.has_statement && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                label="POS Card revenue"
                value={`RM ${(recon.pos_card_revenue || 0).toFixed(2)}`}
                sub="From sales.payments · method Card"
              />
              <Stat
                label="Bank gross"
                value={`RM ${(totals.gross || 0).toFixed(2)}`}
                sub={`${totals.txn_count || 0} txns`}
              />
              <Stat
                label="Variance"
                value={`RM ${(recon.variance || 0).toFixed(2)}`}
                tone={Math.abs(recon.variance || 0) < 1 ? "pos" : "neg"}
                sub="POS card − Bank gross"
              />
              <Stat
                label="Effective MDR"
                value={`${(totals.mdr_pct || 0).toFixed(3)}%`}
                sub={`MDR fee: RM ${(totals.disc || 0).toFixed(2)}`}
              />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="merchant-recon-table">
              <thead className="text-left text-ink-soft border-b border-ink/10">
                <tr>
                  <th className="py-2 overline">Date</th>
                  <th className="py-2 overline text-right">POS Card</th>
                  <th className="py-2 overline text-right">Bank Gross</th>
                  <th className="py-2 overline text-right">MDR fee</th>
                  <th className="py-2 overline text-right">Bank Net</th>
                  <th className="py-2 overline text-right">Variance</th>
                  <th className="py-2 overline text-right">MDR%</th>
                </tr>
              </thead>
              <tbody>
                {reconLoading && (
                  <tr><td colSpan="7" className="py-4 text-center text-ink-soft">
                    <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
                  </td></tr>
                )}
                {!reconLoading && days.length === 0 && (
                  <tr><td colSpan="7" className="py-6 text-center text-ink-soft" data-testid="merchant-recon-empty">
                    No merchant statement saved for <strong className="text-ink">{month}</strong> yet.
                    Upload the PDF above to populate this table.
                  </td></tr>
                )}
                {!reconLoading && days.map((r) => {
                  const varAbs = Math.abs(r.variance || 0);
                  const varTone = varAbs < 1 ? "text-emerald-700" : varAbs < 10 ? "text-amber-700" : "text-red-700";
                  return (
                    <tr key={r.date} className="border-t border-ink/5" data-testid={`merchant-recon-row-${r.date}`}>
                      <td className="py-2 text-ink whitespace-nowrap">{r.date}</td>
                      <td className="py-2 text-right whitespace-nowrap">RM {r.pos_card_revenue.toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap">RM {r.bank_gross.toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap text-red-700">RM {r.bank_disc.toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap">RM {r.bank_net.toFixed(2)}</td>
                      <td className={`py-2 text-right whitespace-nowrap ${varTone}`}>RM {r.variance.toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap text-ink-soft">{r.mdr_pct.toFixed(3)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MDR fee ↔ CIMB bank credit matcher — one row per merchant-statement
          day. Auto-matches when a CIMB credit equals the day's Net on d,
          d+1 or d+2 (Public Bank settles same-day → T+2). */}
      {bankMatches?.has_statement && (
        <div className="mt-2" data-testid="merchant-bank-match-block">
          <div className="flex items-baseline justify-between mb-2">
            <p className="overline text-ink-soft">MDR fee ↔ CIMB bank credit</p>
            {bankMatches.stats && (
              <p className="text-xs text-ink-soft">
                <span className="text-emerald-700 font-semibold">{bankMatches.stats.matched} matched</span>
                {bankMatches.stats.manual > 0 && <span className="ml-2 text-sky-700">· {bankMatches.stats.manual} manual</span>}
                {bankMatches.stats.no_match > 0 && <span className="ml-2 text-ink-soft">· {bankMatches.stats.no_match} no-match</span>}
                {bankMatches.stats.unmatched > 0 && <span className="ml-2 text-amber-700 font-semibold">· {bankMatches.stats.unmatched} unmatched</span>}
                <span className="ml-2">of {bankMatches.stats.total}</span>
                {bankMatches.totals?.mdr_pct != null && (
                  <span className="ml-3 text-ink">
                    · effective MDR <strong>{bankMatches.totals.mdr_pct.toFixed(3)}%</strong>
                  </span>
                )}
                {bankMatches.totals?.variance != null && Math.abs(bankMatches.totals.variance) > 0.01 && (
                  <span className={`ml-2 font-semibold ${bankMatches.totals.variance >= 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    · variance RM {bankMatches.totals.variance.toFixed(2)}
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="merchant-bank-match-table">
              <thead className="text-left text-ink-soft border-b border-ink/10">
                <tr>
                  <th className="py-2 overline">Stmt Date</th>
                  <th className="py-2 overline text-right">Gross</th>
                  <th className="py-2 overline text-right">MDR</th>
                  <th className="py-2 overline text-right">Net</th>
                  <th className="py-2 overline">Status</th>
                  <th className="py-2 overline">CIMB Date</th>
                  <th className="py-2 overline text-right">CIMB Amount</th>
                  <th className="py-2 overline">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bankMatchLoading && (
                  <tr><td colSpan="8" className="py-4 text-center text-ink-soft">
                    <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
                  </td></tr>
                )}
                {!bankMatchLoading && (bankMatches?.matches || []).length === 0 && (
                  <tr><td colSpan="8" className="py-6 text-center text-ink-soft" data-testid="merchant-bank-match-empty">
                    No merchant-statement days for {month}. Upload the Public Bank statement above.
                  </td></tr>
                )}
                {!bankMatchLoading && (bankMatches?.matches || []).map((m) => {
                  const badgeCls =
                    m.status === "matched"   ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                    m.status === "manual"    ? "bg-sky-50 text-sky-800 border-sky-200" :
                    m.status === "no_match"  ? "bg-ink/5 text-ink-soft border-ink/15" :
                                               "bg-amber-50 text-amber-900 border-amber-200";
                  const badgeLbl =
                    m.status === "matched"   ? "MATCHED" :
                    m.status === "manual"    ? "MANUAL"  :
                    m.status === "no_match"  ? "NO MATCH": "UNMATCHED";
                  const bt = m.bank_txn;
                  const isExpandable = (m.status === "matched" || m.status === "manual")
                    && Array.isArray(m.pos_orders) && m.pos_orders.length > 0;
                  const isExpanded = isExpandable && expandedStmtDate === m.stmt_date;
                  return (
                    <React.Fragment key={m.stmt_date}>
                    <tr
                      className={`border-t border-ink/5 align-top ${isExpandable ? "cursor-pointer hover:bg-emerald-50/40" : ""}`}
                      data-testid={`mb-match-row-${m.stmt_date}`}
                      onClick={() => isExpandable && setExpandedStmtDate(isExpanded ? null : m.stmt_date)}
                    >
                      <td className="py-2 text-ink whitespace-nowrap">
                        {isExpandable && (
                          <span data-testid={`mb-match-expand-${m.stmt_date}`} className="inline-block mr-1 align-middle">
                            {isExpanded
                              ? <ChevronDown size={12} className="text-emerald-700 inline" />
                              : <ChevronRight size={12} className="text-ink-soft inline" />}
                          </span>
                        )}
                        {m.stmt_date}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap font-mono">
                        RM {m.gross.toFixed(2)}
                        {isExpandable && (
                          <span className="ml-1 text-ink-soft text-[10px]">· {m.pos_orders.length} orders</span>
                        )}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap font-mono text-red-700">RM {m.mdr_fee.toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap font-mono">RM {m.net.toFixed(2)}</td>
                      <td className="py-2 whitespace-nowrap">
                        <span
                          data-testid={`mb-match-status-${m.stmt_date}`}
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] border ${badgeCls}`}
                        >
                          {badgeLbl}
                        </span>
                      </td>
                      <td className="py-2 whitespace-nowrap text-ink">
                        {bt ? bt.date : "—"}
                        {m.day_offset != null && m.day_offset !== 0 && (
                          <span className="ml-1 text-ink-soft text-[10px]">({m.day_offset > 0 ? "+" : ""}{m.day_offset}d)</span>
                        )}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap font-mono">{bt ? `RM ${bt.amount.toFixed(2)}` : "—"}</td>
                      <td className="py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {m.status === "unmatched" && (
                          <div className="flex flex-col gap-1">
                            {(m.candidates || []).length === 0 ? (
                              <span className="text-ink-soft text-[11px]">No CIMB credits within ±3 days · RM {m.net.toFixed(2)}</span>
                            ) : (
                              (m.candidates || []).map((c) => (
                                <button
                                  key={c.txn_id}
                                  type="button"
                                  data-testid={`mb-match-link-${m.stmt_date}-${c.txn_id}`}
                                  onClick={() => linkMerchantDay(m.stmt_date, c.txn_id)}
                                  className="text-left text-[11px] hover:bg-sage/30 px-1.5 py-0.5 rounded-sm border border-transparent hover:border-matcha-dark/20"
                                  title={`Amount diff RM ${c.amount_diff.toFixed(2)} · ${c.day_diff}d off · ${c.narrative}`}
                                >
                                  <span className="font-mono text-ink">{c.date}</span>
                                  <span className="text-ink-soft"> · RM {c.amount.toFixed(2)}</span>
                                  <span className={`text-[10px] ml-1 ${Math.abs(c.amount_diff) < 0.5 ? "text-emerald-700" : "text-ink-soft"}`}>
                                    ({c.amount_diff >= 0 ? "+" : ""}{c.amount_diff.toFixed(2)})
                                  </span>
                                  {c.narrative && <span className="text-ink-soft/70 text-[10px] ml-1 block truncate max-w-xs">{c.narrative}</span>}
                                </button>
                              ))
                            )}
                            <button
                              type="button"
                              data-testid={`mb-match-nomatch-${m.stmt_date}`}
                              onClick={() => markMerchantNoMatch(m.stmt_date)}
                              className="text-left text-[11px] text-ink-soft hover:text-ink px-1.5 py-0.5"
                            >
                              Mark as no-bank-match…
                            </button>
                          </div>
                        )}
                        {(m.status === "manual" || m.status === "no_match") && (
                          <button
                            type="button"
                            data-testid={`mb-match-clear-${m.stmt_date}`}
                            onClick={() => clearMerchantOverride(m.stmt_date)}
                            className="text-[11px] text-ink-soft hover:text-ink underline"
                          >
                            Clear override
                          </button>
                        )}
                        {m.status === "matched" && (
                          <span className="text-[11px] text-emerald-700">Auto-matched</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-emerald-50/40" data-testid={`mb-match-detail-${m.stmt_date}`}>
                        <td colSpan="8" className="px-4 py-3">
                          <p className="overline text-emerald-800 mb-2">
                            POS Card orders on {m.stmt_date} · {m.pos_orders.length} orders · gross RM {m.gross.toFixed(2)}
                          </p>
                          <table className="w-full text-[11px]">
                            <thead className="text-left text-ink-soft">
                              <tr>
                                <th className="py-1 overline">Order #</th>
                                <th className="py-1 overline">Invoice #</th>
                                <th className="py-1 overline">Order Type</th>
                                <th className="py-1 overline">Payment Mode</th>
                                <th className="py-1 overline">Status</th>
                                <th className="py-1 overline text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {m.pos_orders.map((o) => (
                                <tr key={o.order_id} className="border-t border-ink/5" data-testid={`mb-match-detail-order-${o.order_id}`}>
                                  <td className="py-1 font-mono text-ink whitespace-nowrap">{o.order_no || "—"}</td>
                                  <td className="py-1 font-mono text-ink-soft whitespace-nowrap">{o.invoice_no || "—"}</td>
                                  <td className="py-1 text-ink whitespace-nowrap">{o.order_type || "—"}</td>
                                  <td className="py-1 text-ink-soft whitespace-nowrap">{o.payment_mode}</td>
                                  <td className="py-1 text-ink-soft whitespace-nowrap">{o.order_status || "—"}</td>
                                  <td className="py-1 text-right whitespace-nowrap font-mono text-ink">RM {Number(o.total_price || 0).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t border-emerald-700/20">
                                <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Sum</td>
                                <td className="py-1 text-right font-mono font-semibold text-ink">RM {m.gross.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">MDR fee</td>
                                <td className="py-1 text-right font-mono text-red-700">RM {m.mdr_fee.toFixed(2)} <span className="text-ink-soft">({m.mdr_pct.toFixed(3)}%)</span></td>
                              </tr>
                              <tr>
                                <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Net paid</td>
                                <td className="py-1 text-right font-mono font-semibold text-emerald-700">RM {m.net.toFixed(2)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </section>
  );
}

// ---------------- GEBME Order History (source of truth) ----------------
// Standalone section — the pos_transactions collection imported here feeds
// EVERY downstream matcher (Foodpanda invoice-level, Grab settlement-level,
// CIMB day match). Kept independent so admins never have to hunt for it
// inside another vendor section.
function OrderHistorySection({ month, setMonth, onImported }) {
  const [summary, setSummary] = React.useState(null);   // {row_count, by_mode[]}
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const fileRef = React.useRef(null);

  const loadSummary = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/mgmt/pos/order-history?month=${month}`);
      const modes = {};
      let total = 0;
      (data || []).forEach((r) => {
        modes[r.payment_mode] = (modes[r.payment_mode] || 0) + 1;
        total += Number(r.total_price || 0);
      });
      setSummary({
        row_count: data?.length || 0,
        total_revenue: total,
        by_mode: Object.entries(modes)
          .map(([mode, n]) => ({ mode, count: n }))
          .sort((a, b) => b.count - a.count),
      });
    } catch {
      // Silent — Order History might not be imported yet for this month.
      setSummary({ row_count: 0, total_revenue: 0, by_mode: [] });
    }
  }, [month]);

  React.useEffect(() => { loadSummary(); }, [loadSummary]);

  const uploadOrderHistory = async (f) => {
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: parsed } = await api.post(
        "/mgmt/pos/order-history/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const modes = (parsed.by_payment_mode || [])
        .map((m) => `${m.mode}: ${m.count}`).join("\n");
      if (!window.confirm(
        `Import GEBME Order History?\n\n` +
        `File: ${f.name}\n` +
        `Rows: ${parsed.row_count}\n` +
        `Dates: ${parsed.date_from} → ${parsed.date_to}\n\n` +
        `Payment modes:\n${modes}\n\n` +
        `Existing orders with the same Order ID will be updated.`
      )) { setUploading(false); return; }
      const { data: committed } = await api.post(
        "/mgmt/pos/order-history/commit",
        { rows: parsed.rows, filename: parsed.filename || f.name },
      );
      setErr(`Imported · ${committed.inserted} new · ${committed.updated} updated`);
      // Bump month to file's period if the imported data covers a different month.
      const inMonth = parsed.date_from?.slice(0, 7);
      if (inMonth && inMonth !== month) setMonth(inMonth);
      await loadSummary();
      onImported?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const populated = summary && summary.row_count > 0;

  return (
    <section
      className="mt-10 rounded-sm border border-amber-700/25 bg-amber-50/30 p-6 space-y-4 shadow-sm"
      data-testid="order-history-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-ink/10 pb-4">
        <div>
          <p className="overline text-amber-700">Section 3 · GEBME Order History</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Source of truth · every matcher joins against this
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Drop the <strong className="text-ink">Order History - Gebme - *.xlsx</strong> export from GEBME —
            each per-order row is stored in <code className="text-[11px]">pos_transactions</code> and unlocks
            invoice-level matching for Foodpanda, settlement-level matching for Grab, and daily reconciliation
            against CIMB. <strong>Upload this FIRST before Foodpanda / Grab / CIMB.</strong>
            Idempotent by Order ID.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline text-ink-soft">Month</label>
          <input
            type="month"
            data-testid="oh-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-sm text-ink"
          />
        </div>
      </div>

      {err && (
        <div
          data-testid="oh-err"
          className="rounded-sm border border-amber-700/30 bg-amber-50 text-amber-900 text-sm px-3 py-2"
        >
          {err}
        </div>
      )}

      <div
        data-testid="oh-drop-zone"
        className={`rounded-sm border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
          populated ? "border-ink/15 bg-white/60 hover:bg-white" : "border-amber-700/40 bg-white/70 hover:bg-white"
        }`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) uploadOrderHistory(f);
        }}
      >
        <Upload className="mx-auto text-amber-700" size={32} />
        <p className="mt-3 text-ink">
          {populated ? (
            <>
              <strong>{summary.row_count}</strong> orders imported for {month} ·
              <span className="ml-2 text-ink-soft">
                {summary.by_mode.slice(0, 4).map((m) => `${m.mode} (${m.count})`).join(" · ")}
                {summary.by_mode.length > 4 ? ` · +${summary.by_mode.length - 4} more` : ""}
              </span>
            </>
          ) : (
            <><strong>Drop GEBME Order History (xlsx/csv) here</strong> or click to browse</>
          )}
        </p>
        <p className="text-ink-soft text-xs mt-1">
          Idempotent by Order ID · 100% offline parser
        </p>
        <input
          ref={fileRef}
          data-testid="oh-file-input"
          type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => uploadOrderHistory(e.target.files?.[0])}
        />
        {uploading && (
          <p className="mt-3 inline-flex items-center gap-2 text-amber-700">
            <Loader2 className="animate-spin" size={16} />
            Importing…
          </p>
        )}
      </div>
    </section>
  );
}




// ---------------- Foodpanda Payment Advice section ----------------
// One PDF = one bank transfer. Batch upload supported: dropping multiple
// PDFs at once parses+commits each in sequence, then reloads the recon.
function FoodpandaPayoutSection({ month, setMonth, ohBump = 0 }) {
  const [payouts, setPayouts] = React.useState([]);
  const [recon, setRecon] = React.useState(null);
  const [reconLoading, setReconLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const [err, setErr] = React.useState("");
  const fileRef = React.useRef(null);
  // Per-invoice match state (matched / unmatched with candidates)
  const [invoiceMatches, setInvoiceMatches] = React.useState(null);
  const [invMatchLoading, setInvMatchLoading] = React.useState(false);
  const [expandedInvoice, setExpandedInvoice] = React.useState(null); // invoice_no

  const loadPayouts = React.useCallback(async () => {
    try {
      const { data } = await api.get("/mgmt/bank/foodpanda-payouts");
      setPayouts(data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }, []);

  const loadRecon = React.useCallback(async () => {
    setReconLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/foodpanda-recon?month=${month}`);
      setRecon(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setReconLoading(false);
    }
  }, [month]);

  const loadInvoiceMatches = React.useCallback(async () => {
    setInvMatchLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/foodpanda-invoice-matches?month=${month}`);
      setInvoiceMatches(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setInvMatchLoading(false);
    }
  }, [month]);

  React.useEffect(() => { loadPayouts(); }, [loadPayouts]);
  React.useEffect(() => { loadRecon(); }, [loadRecon]);
  React.useEffect(() => { loadInvoiceMatches(); }, [loadInvoiceMatches]);
  // When GEBME Order History is (re)imported at the parent, refresh recon +
  // invoice-level matches so the newly-imported pos_transactions flow through.
  React.useEffect(() => {
    if (ohBump === 0) return;
    loadRecon();
    loadInvoiceMatches();
  }, [ohBump, loadRecon, loadInvoiceMatches]);

  const linkInvoice = async (invoice_no, order_id) => {
    if (!order_id) return;
    try {
      await api.post("/mgmt/bank/foodpanda-invoice-link", {
        invoice_no, action: "link", order_id,
      });
      await loadInvoiceMatches();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const markNoMatch = async (invoice_no) => {
    const note = window.prompt(
      `Mark invoice ${invoice_no} as no-POS-match?\n\n` +
      `Reason (optional):`, "",
    );
    if (note === null) return;
    try {
      await api.post("/mgmt/bank/foodpanda-invoice-link", {
        invoice_no, action: "no_match", note: note || "",
      });
      await loadInvoiceMatches();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const clearInvoice = async (invoice_no) => {
    if (!window.confirm(`Clear the admin override on invoice ${invoice_no}?`)) return;
    try {
      await api.post("/mgmt/bank/foodpanda-invoice-link", {
        invoice_no, action: "clear",
      });
      await loadInvoiceMatches();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setErr("");
    setUploading(true);
    const total = files.length;
    let saved = 0;
    let dupes = 0;
    let failed = 0;
    for (let i = 0; i < total; i++) {
      const f = files[i];
      setUploadProgress({ i: i + 1, total, name: f.name });
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data: parsed } = await api.post(
          "/mgmt/bank/parse-foodpanda-payadv", fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (!parsed.document_no || !parsed.payment_date) {
          failed += 1;
          continue;
        }
        // idempotent by document_no on the server — but log dupes here too
        const alreadySeen = payouts.some((p) => p.document_no === parsed.document_no);
        await api.post("/mgmt/bank/foodpanda-payouts", {
          document_no: parsed.document_no,
          payment_date: parsed.payment_date,
          backend_id: parsed.backend_id || "",
          beneficiary_name: parsed.beneficiary_name || "",
          beneficiary_bank_acc: parsed.beneficiary_bank_acc || "",
          currency: parsed.currency || "MYR",
          payment_amount: parsed.payment_amount || 0,
          total_amount: parsed.total_amount || 0,
          invoices: parsed.invoices || [],
          filename: parsed.filename || f.name,
        });
        if (alreadySeen) dupes += 1;
        else saved += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setUploadProgress(null);
    setUploading(false);
    await loadPayouts();
    await loadRecon();
    await loadInvoiceMatches();
    // Bump the month to whichever payout was just saved (if it lands in a
    // different month than the one shown).
    const firstPd = files.length && (await api.get("/mgmt/bank/foodpanda-payouts"))
      .data?.[0]?.payment_date;
    if (firstPd && firstPd.slice(0, 7) !== month) setMonth(firstPd.slice(0, 7));
    if (failed) setErr(`${failed} file${failed > 1 ? "s" : ""} failed to parse — is it a Foodpanda PAYADV PDF?`);
    else setErr(`${saved} new · ${dupes} already-imported · 0 failed`);
  };

  const removePayout = async (id) => {
    if (!window.confirm("Delete this Foodpanda payout? The recon will refresh.")) return;
    try {
      await api.delete(`/mgmt/bank/foodpanda-payouts/${id}`);
      await loadPayouts();
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const rows = recon?.rows || [];
  const totals = recon?.totals || {};
  const monthPayouts = payouts.filter((p) => (p.payment_date || "").startsWith(month));

  return (
    <section
      className="mt-10 rounded-sm border border-pink-700/25 bg-pink-50/30 p-6 space-y-4 shadow-sm"
      data-testid="foodpanda-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-ink/10 pb-4">
        <div>
          <p className="overline text-pink-700">Section 4 · Foodpanda Payment Advice</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Foodpanda invoice ↔ GEBME POS ↔ CIMB bank credit
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Drop every <strong className="text-ink">PAYADV_YYYYMMDD_*.pdf</strong> Foodpanda emails —
            we extract the net payout, invoice number and payment date, then reconcile it against
            your GEBME Foodpanda channel gross AND the CIMB bank credits tagged
            <CategoryBadge cat="Foodpanda" className="ml-1 align-middle" />.
            Drop many at once — duplicates are auto-skipped by document number.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline text-ink-soft">Month</label>
          <input
            type="month"
            data-testid="foodpanda-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls + " w-40"}
          />
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid="foodpanda-err">
          {err}
        </p>
      )}

      <div
        data-testid="foodpanda-drop-zone"
        className="border-2 border-dashed border-pink-500/30 rounded-sm p-8 text-center bg-white/40 hover:bg-white/70 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length) uploadFiles(files);
        }}
      >
        <input
          ref={fileRef}
          data-testid="foodpanda-file-input"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
        />
        <Upload className="mx-auto text-pink-600" size={32} />
        <p className="mt-3 text-ink">
          <strong>Drop Foodpanda PAYADV PDF(s) here</strong> or click to browse
        </p>
        <p className="text-ink-soft text-xs mt-1">
          Multi-file supported · idempotent by Document No. · 100% offline parser
        </p>
        {uploading && uploadProgress && (
          <p className="mt-3 inline-flex items-center gap-2 text-pink-700">
            <Loader2 className="animate-spin" size={16} />
            Parsing {uploadProgress.i}/{uploadProgress.total} · {uploadProgress.name}
          </p>
        )}
      </div>

      {/* Recon totals scorecards */}
      {recon && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat
            label="POS Gross"
            value={`RM ${(totals.pos_gross || 0).toFixed(2)}`}
            sub="GEBME · Foodpanda channel"
          />
          <Stat
            label="Invoice Net"
            value={`RM ${(totals.invoice_net || 0).toFixed(2)}`}
            sub={`${monthPayouts.reduce((n, p) => n + (p.invoices?.length || 0), 0)} invoices`}
          />
          <Stat
            label="Bank Payout"
            value={`RM ${(totals.payout_amount || 0).toFixed(2)}`}
            sub={`${monthPayouts.length} transfers`}
          />
          <Stat
            label="Bank Credited"
            value={`RM ${(totals.bank_credit || 0).toFixed(2)}`}
            sub="CIMB · tagged Foodpanda"
          />
          <Stat
            label="Commission %"
            value={
              totals.effective_commission_pct == null
                ? "—"
                : `${totals.effective_commission_pct.toFixed(2)}%`
            }
            tone={(totals.effective_commission_pct || 0) > 30 ? "neg" : "pos"}
            sub="1 − Invoice ÷ POS"
          />
        </div>
      )}

      {/* Recon table — per-day, all four columns */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="foodpanda-recon-table">
          <thead className="text-left text-ink-soft border-b border-ink/10">
            <tr>
              <th className="py-2 overline">Date</th>
              <th className="py-2 overline text-right">POS Gross</th>
              <th className="py-2 overline text-right">Invoice Net</th>
              <th className="py-2 overline text-right">POS − Invoice</th>
              <th className="py-2 overline text-right">Payout</th>
              <th className="py-2 overline text-right">Bank Credit</th>
              <th className="py-2 overline text-right">Bank − Payout</th>
            </tr>
          </thead>
          <tbody>
            {reconLoading && (
              <tr><td colSpan="7" className="py-4 text-center text-ink-soft">
                <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
              </td></tr>
            )}
            {!reconLoading && rows.length === 0 && (
              <tr><td colSpan="7" className="py-6 text-center text-ink-soft" data-testid="foodpanda-recon-empty">
                No Foodpanda activity for <strong className="text-ink">{month}</strong> yet.
                Drop payment advice PDFs above, import GEBME sales, and upload the CIMB statement to populate this table.
              </td></tr>
            )}
            {!reconLoading && rows.map((r) => {
              const vPI = r.variance_pos_invoice || 0;
              const vPB = r.variance_payout_bank || 0;
              const tonePI = Math.abs(vPI) < 0.5 ? "text-emerald-700" : "text-amber-700";
              const tonePB = Math.abs(vPB) < 0.5 ? "text-emerald-700" : "text-amber-700";
              return (
                <tr key={r.date} className="border-t border-ink/5" data-testid={`foodpanda-recon-row-${r.date}`}>
                  <td className="py-2 text-ink whitespace-nowrap">{r.date}</td>
                  <td className="py-2 text-right whitespace-nowrap">RM {r.pos_gross.toFixed(2)}</td>
                  <td className="py-2 text-right whitespace-nowrap">RM {r.invoice_net.toFixed(2)}</td>
                  <td className={`py-2 text-right whitespace-nowrap ${tonePI}`}>{vPI >= 0 ? "+" : ""}RM {vPI.toFixed(2)}</td>
                  <td className="py-2 text-right whitespace-nowrap">RM {r.payout_amount.toFixed(2)}</td>
                  <td className="py-2 text-right whitespace-nowrap">RM {r.bank_credit.toFixed(2)}</td>
                  <td className={`py-2 text-right whitespace-nowrap ${tonePB}`}>{vPB >= 0 ? "+" : ""}RM {vPB.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invoice-level matcher — pairs every PAYADV invoice with a GEBME
          order (payment_mode == offline-foodpanda). Requires the Order
          History import above; empty state guides the admin there. */}
      <div data-testid="foodpanda-invoice-matches">
        <div className="flex items-baseline justify-between mb-2">
          <p className="overline text-ink-soft">Invoice ↔ POS order match</p>
          {invoiceMatches?.stats && (
            <p className="text-xs text-ink-soft">
              <span className="text-emerald-700 font-semibold">{invoiceMatches.stats.matched} matched</span>
              {invoiceMatches.stats.manual > 0 && <span className="ml-2 text-sky-700">· {invoiceMatches.stats.manual} manual</span>}
              {invoiceMatches.stats.no_match > 0 && <span className="ml-2 text-ink-soft">· {invoiceMatches.stats.no_match} no-match</span>}
              {invoiceMatches.stats.unmatched > 0 && <span className="ml-2 text-amber-700 font-semibold">· {invoiceMatches.stats.unmatched} unmatched</span>}
              <span className="ml-2">of {invoiceMatches.stats.total}</span>
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="foodpanda-invoice-match-table">
            <thead className="text-left text-ink-soft border-b border-ink/10">
              <tr>
                <th className="py-2 overline">Invoice #</th>
                <th className="py-2 overline">Issued</th>
                <th className="py-2 overline text-right">Net</th>
                <th className="py-2 overline">Status</th>
                <th className="py-2 overline">GEBME Order #</th>
                <th className="py-2 overline">Invoice #</th>
                <th className="py-2 overline text-right">Gross</th>
                <th className="py-2 overline text-right">Comm %</th>
                <th className="py-2 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invMatchLoading && (
                <tr><td colSpan="9" className="py-4 text-center text-ink-soft">
                  <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
                </td></tr>
              )}
              {!invMatchLoading && (invoiceMatches?.matches || []).length === 0 && (
                <tr><td colSpan="9" className="py-6 text-center text-ink-soft" data-testid="foodpanda-invoice-match-empty">
                  Upload Foodpanda PAYADV PDFs above to populate this table.
                </td></tr>
              )}
              {!invMatchLoading && (invoiceMatches?.matches || []).map((m) => {
                const badgeCls =
                  m.status === "matched"   ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                  m.status === "manual"    ? "bg-sky-50 text-sky-800 border-sky-200" :
                  m.status === "no_match"  ? "bg-ink/5 text-ink-soft border-ink/15" :
                                             "bg-amber-50 text-amber-900 border-amber-200";
                const badgeLbl =
                  m.status === "matched"   ? "MATCHED" :
                  m.status === "manual"    ? "MANUAL" :
                  m.status === "no_match"  ? "NO MATCH" : "UNMATCHED";
                const po = m.pos_order;
                const isExpandable = (m.status === "matched" || m.status === "manual") && po;
                const isExpanded = isExpandable && expandedInvoice === m.invoice_no;
                return (
                  <React.Fragment key={m.invoice_no}>
                  <tr
                    className={`border-t border-ink/5 align-top ${isExpandable ? "cursor-pointer hover:bg-pink-50/50" : ""}`}
                    data-testid={`inv-match-row-${m.invoice_no}`}
                    onClick={() => isExpandable && setExpandedInvoice(isExpanded ? null : m.invoice_no)}
                  >
                    <td className="py-2 font-mono text-ink whitespace-nowrap">
                      {isExpandable && (
                        <span data-testid={`inv-match-expand-${m.invoice_no}`} className="inline-block mr-1 align-middle">
                          {isExpanded
                            ? <ChevronDown size={12} className="text-pink-700 inline" />
                            : <ChevronRight size={12} className="text-ink-soft inline" />}
                        </span>
                      )}
                      {m.invoice_no}
                    </td>
                    <td className="py-2 text-ink whitespace-nowrap">{m.issuance_date}</td>
                    <td className="py-2 text-right whitespace-nowrap font-mono">RM {(m.amount || 0).toFixed(2)}</td>
                    <td className="py-2 whitespace-nowrap">
                      <span
                        data-testid={`inv-match-status-${m.invoice_no}`}
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] border ${badgeCls}`}
                      >
                        {badgeLbl}
                      </span>
                    </td>
                    <td className="py-2 font-mono text-ink whitespace-nowrap">{po?.order_no || "—"}</td>
                    <td className="py-2 font-mono text-ink-soft whitespace-nowrap">{po?.invoice_no || "—"}</td>
                    <td className="py-2 text-right whitespace-nowrap font-mono">{po ? `RM ${po.total_price.toFixed(2)}` : "—"}</td>
                    <td className="py-2 text-right whitespace-nowrap text-ink-soft">
                      {m.commission_pct != null ? `${m.commission_pct}%` : "—"}
                    </td>
                    <td className="py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {m.status === "unmatched" && (
                        <div className="flex flex-col gap-1">
                          {(m.candidates || []).length === 0 ? (
                            <span className="text-ink-soft text-[11px]">No candidates on/near {m.issuance_date}</span>
                          ) : (
                            (m.candidates || []).map((c) => (
                              <button
                                key={c.order_id}
                                type="button"
                                data-testid={`inv-match-link-${m.invoice_no}-${c.order_id}`}
                                onClick={() => linkInvoice(m.invoice_no, c.order_id)}
                                className="text-left text-[11px] hover:bg-sage/30 px-1.5 py-0.5 rounded-sm border border-transparent hover:border-matcha-dark/20"
                                title={`Score ${c.score} · ${c.day_diff}d off · commission ${c.commission_pct}%`}
                              >
                                <span className="font-mono text-ink">#{c.order_no || "?"}</span>
                                <span className="text-ink-soft"> · {c.order_date} · RM {Number(c.total_price).toFixed(2)}</span>
                                <span className="text-ink-soft"> · {c.payment_mode}</span>
                                <span className="text-ink-soft"> ({c.commission_pct}%)</span>
                              </button>
                            ))
                          )}
                          <button
                            type="button"
                            data-testid={`inv-match-nomatch-${m.invoice_no}`}
                            onClick={() => markNoMatch(m.invoice_no)}
                            className="text-left text-[11px] text-ink-soft hover:text-ink px-1.5 py-0.5"
                          >
                            Mark as no-POS-match…
                          </button>
                        </div>
                      )}
                      {(m.status === "manual" || m.status === "no_match") && (
                        <button
                          type="button"
                          data-testid={`inv-match-clear-${m.invoice_no}`}
                          onClick={() => clearInvoice(m.invoice_no)}
                          className="text-[11px] text-ink-soft hover:text-ink underline"
                        >
                          Clear override
                        </button>
                      )}
                      {m.status === "matched" && (
                        <span className="text-[11px] text-emerald-700">Auto-matched</span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-pink-50/40" data-testid={`inv-match-detail-${m.invoice_no}`}>
                      <td colSpan="9" className="px-4 py-3">
                        <p className="overline text-pink-800 mb-2">
                          Foodpanda invoice {m.invoice_no} ↔ GEBME Order #{po.order_no}
                          {m.payment_date && <span className="text-ink-soft"> · paid to bank {m.payment_date}</span>}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-[11px]">
                          <div><span className="text-ink-soft">Foodpanda Issued</span><br/><strong>{m.issuance_date}</strong></div>
                          <div><span className="text-ink-soft">Doc No.</span><br/><span className="font-mono">{m.document_no || "—"}</span></div>
                          <div><span className="text-ink-soft">Invoice Net</span><br/><strong className="font-mono">RM {m.amount.toFixed(2)}</strong></div>
                          <div><span className="text-ink-soft">Payment Date</span><br/>{m.payment_date || "—"}</div>
                          <div className="col-span-2 mt-2"><span className="text-ink-soft">GEBME Order</span><br/>
                            <span className="font-mono text-ink">#{po.order_no}</span>
                            <span className="text-ink-soft"> · Invoice # </span>
                            <span className="font-mono">{po.invoice_no}</span>
                          </div>
                          <div><span className="text-ink-soft">Order Date</span><br/><strong>{po.order_date}</strong></div>
                          <div><span className="text-ink-soft">Order Type</span><br/><strong>{po.order_type || "—"}</strong></div>
                          <div><span className="text-ink-soft">Payment Mode</span><br/><span className="text-ink">{po.payment_mode}</span></div>
                          <div><span className="text-ink-soft">Status</span><br/>{po.order_status || "—"}</div>
                          <div><span className="text-ink-soft">POS Gross</span><br/><strong className="font-mono">RM {Number(po.total_price).toFixed(2)}</strong></div>
                          <div><span className="text-ink-soft">Commission</span><br/>
                            <strong className="font-mono text-red-700">RM {(Number(po.total_price) - m.amount).toFixed(2)}</strong>
                            <span className="text-ink-soft"> ({m.commission_pct}%)</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Saved payouts list */}
      {monthPayouts.length > 0 && (
        <div className="mt-4">
          <p className="overline text-ink-soft mb-2">Payouts saved for {month}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="foodpanda-payouts-table">
              <thead className="text-left text-ink-soft border-b border-ink/10">
                <tr>
                  <th className="py-1.5 overline">Payment Date</th>
                  <th className="py-1.5 overline">Document No.</th>
                  <th className="py-1.5 overline">Invoice(s)</th>
                  <th className="py-1.5 overline text-right">Amount</th>
                  <th className="py-1.5 overline text-right">Bank Acc</th>
                  <th className="py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {monthPayouts.map((p) => (
                  <tr key={p.id} className="border-t border-ink/5" data-testid={`foodpanda-payout-row-${p.id}`}>
                    <td className="py-1.5 text-ink whitespace-nowrap">{p.payment_date}</td>
                    <td className="py-1.5 font-mono text-ink whitespace-nowrap">{p.document_no}</td>
                    <td className="py-1.5 text-ink-soft">
                      {(p.invoices || []).map((inv) => (
                        <span key={inv.invoice_no} className="mr-2">
                          <span className="font-mono">{inv.invoice_no}</span>
                          <span className="text-ink-soft/60"> ({inv.issuance_date})</span>
                        </span>
                      ))}
                    </td>
                    <td className="py-1.5 text-right whitespace-nowrap font-mono">RM {(p.payment_amount || 0).toFixed(2)}</td>
                    <td className="py-1.5 text-right whitespace-nowrap font-mono text-ink-soft">{p.beneficiary_bank_acc || "—"}</td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        data-testid={`foodpanda-payout-delete-${p.id}`}
                        onClick={() => removePayout(p.id)}
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        title="Delete this payout"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------- Grab Transfers section ----------------
// One CSV = all settlements for the period. Grab batches a whole day of
// Grab orders per transfer, so matching is DAY-level (not per-order).
function GrabTransferSection({ month, setMonth, ohBump = 0 }) {
  const [transfers, setTransfers] = React.useState([]);
  const [matches, setMatches] = React.useState(null);
  const [matchLoading, setMatchLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [expandedId, setExpandedId] = React.useState(null);   // settlement_id currently expanded
  const fileRef = React.useRef(null);

  const loadTransfers = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/mgmt/bank/grab-transfers?month=${month}`);
      setTransfers(data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }, [month]);

  const loadMatches = React.useCallback(async () => {
    setMatchLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/grab-transfer-matches?month=${month}`);
      setMatches(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setMatchLoading(false);
    }
  }, [month]);

  React.useEffect(() => { loadTransfers(); }, [loadTransfers]);
  React.useEffect(() => { loadMatches(); }, [loadMatches]);
  // When GEBME Order History is (re)imported at the parent, refresh matches so
  // newly-imported pos_transactions can back the settlements.
  React.useEffect(() => {
    if (ohBump === 0) return;
    loadMatches();
  }, [ohBump, loadMatches]);

  const uploadCSV = async (f) => {
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: parsed } = await api.post(
        "/mgmt/bank/grab-transfers/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!window.confirm(
        `Import Grab Transfers?\n\n` +
        `File: ${f.name}\n` +
        `Rows: ${parsed.row_count}\n` +
        `Dates: ${parsed.date_from} → ${parsed.date_to}\n` +
        `Total net: RM ${parsed.total_net.toFixed(2)}\n\n` +
        `Existing rows with the same Settlement ID will be updated.`
      )) { setUploading(false); return; }
      const { data: committed } = await api.post(
        "/mgmt/bank/grab-transfers/commit",
        { rows: parsed.rows, filename: parsed.filename || f.name },
      );
      setErr(`Imported · ${committed.inserted} new · ${committed.updated} updated`);
      // Bump the month to the file's period if different
      const inMonth = parsed.date_from?.slice(0, 7);
      if (inMonth && inMonth !== month) setMonth(inMonth);
      await loadTransfers();
      await loadMatches();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const linkTransfer = async (settlement_id, pos_date) => {
    if (!pos_date) return;
    try {
      await api.post("/mgmt/bank/grab-transfer-link", {
        settlement_id, action: "link", pos_date,
      });
      await loadMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const markNoMatch = async (settlement_id) => {
    const note = window.prompt(`Mark settlement ${settlement_id} as no-POS-match?\n\nReason (optional):`, "");
    if (note === null) return;
    try {
      await api.post("/mgmt/bank/grab-transfer-link", {
        settlement_id, action: "no_match", note: note || "",
      });
      await loadMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const clearOverride = async (settlement_id) => {
    if (!window.confirm(`Clear the override on settlement ${settlement_id}?`)) return;
    try {
      await api.post("/mgmt/bank/grab-transfer-link", {
        settlement_id, action: "clear",
      });
      await loadMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const removeTransfer = async (id) => {
    if (!window.confirm("Delete this Grab transfer? The recon will refresh.")) return;
    try {
      await api.delete(`/mgmt/bank/grab-transfers/${id}`);
      await loadTransfers();
      await loadMatches();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const stats = matches?.stats || {};
  const totals = matches?.totals || {};

  return (
    <section
      className="mt-10 rounded-sm border border-emerald-700/25 bg-emerald-50/30 p-6 space-y-4 shadow-sm"
      data-testid="grab-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-ink/10 pb-4">
        <div>
          <p className="overline text-emerald-700">Section 5 · Grab Transfers</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Grab settlement CSV ↔ GEBME POS ↔ CIMB bank credit
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Drop the <strong className="text-ink">Transfers_Store_*.csv</strong> Grab exports — we import
            every settlement (Settlement ID, Net Total, Transfer Date), then match each transfer to the
            GEBME <em>offline-grabfood / offline-grabpay</em> orders on the same day.
            Idempotent by Settlement ID.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline text-ink-soft">Month</label>
          <input
            type="month"
            data-testid="grab-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls + " w-40"}
          />
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid="grab-err">
          {err}
        </p>
      )}

      <div
        data-testid="grab-drop-zone"
        className="border-2 border-dashed border-emerald-600/40 rounded-sm p-8 text-center bg-white/40 hover:bg-white/70 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) uploadCSV(f);
        }}
      >
        <input
          ref={fileRef}
          data-testid="grab-file-input"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => uploadCSV(e.target.files?.[0])}
        />
        <Upload className="mx-auto text-emerald-700" size={32} />
        <p className="mt-3 text-ink">
          <strong>Drop Grab Transfers CSV here</strong> or click to browse
        </p>
        <p className="text-ink-soft text-xs mt-1">
          Idempotent by Settlement ID · 100% offline parser
        </p>
        {uploading && (
          <p className="mt-3 inline-flex items-center gap-2 text-emerald-700">
            <Loader2 className="animate-spin" size={16} />
            Importing…
          </p>
        )}
      </div>

      {/* Totals */}
      {matches && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Transfers"
            value={String(transfers.length)}
            sub={`RM ${(totals.transfer_net || 0).toFixed(2)} net`}
          />
          <Stat
            label="Matched POS Gross"
            value={`RM ${(totals.matched_gross || 0).toFixed(2)}`}
            sub="Sum of Grab orders on matched days"
          />
          <Stat
            label="Implied Commission"
            value={`RM ${(totals.implied_commission || 0).toFixed(2)}`}
            tone="neg"
            sub="Gross − Net (Grab keeps this)"
          />
          <Stat
            label="Commission %"
            value={totals.implied_commission_pct == null ? "—" : `${totals.implied_commission_pct.toFixed(2)}%`}
            tone={(totals.implied_commission_pct || 0) > 33 ? "neg" : "pos"}
            sub={`${stats.matched || 0} matched · ${stats.unmatched || 0} unmatched`}
          />
        </div>
      )}

      {/* Match table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="grab-match-table">
          <thead className="text-left text-ink-soft border-b border-ink/10">
            <tr>
              <th className="py-2 overline">Transfer Date</th>
              <th className="py-2 overline">Settlement ID</th>
              <th className="py-2 overline text-right">Net</th>
              <th className="py-2 overline">Status</th>
              <th className="py-2 overline">POS Day</th>
              <th className="py-2 overline text-right">POS Gross</th>
              <th className="py-2 overline text-right">Comm %</th>
              <th className="py-2 overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {matchLoading && (
              <tr><td colSpan="8" className="py-4 text-center text-ink-soft">
                <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
              </td></tr>
            )}
            {!matchLoading && (matches?.matches || []).length === 0 && (
              <tr><td colSpan="8" className="py-6 text-center text-ink-soft" data-testid="grab-match-empty">
                No Grab transfers for <strong className="text-ink">{month}</strong> yet.
                Upload the Grab Transfers CSV above to populate this table.
              </td></tr>
            )}
            {!matchLoading && (matches?.matches || []).map((m) => {
              const badgeCls =
                m.status === "matched"   ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                m.status === "manual"    ? "bg-sky-50 text-sky-800 border-sky-200" :
                m.status === "no_match"  ? "bg-ink/5 text-ink-soft border-ink/15" :
                                           "bg-amber-50 text-amber-900 border-amber-200";
              const badgeLbl =
                m.status === "matched"   ? "MATCHED" :
                m.status === "manual"    ? "MANUAL"  :
                m.status === "no_match"  ? "NO MATCH": "UNMATCHED";
              const isExpandable = (m.status === "matched" || m.status === "manual")
                && Array.isArray(m.pos_orders) && m.pos_orders.length > 0;
              const isExpanded = isExpandable && expandedId === m.settlement_id;
              return (
                <React.Fragment key={m.settlement_id}>
                <tr
                  className={`border-t border-ink/5 align-top ${isExpandable ? "cursor-pointer hover:bg-emerald-50/40" : ""}`}
                  data-testid={`grab-match-row-${m.settlement_id}`}
                  onClick={() => isExpandable && setExpandedId(isExpanded ? null : m.settlement_id)}
                >
                  <td className="py-2 text-ink whitespace-nowrap">
                    {isExpandable && (
                      <span data-testid={`grab-match-expand-${m.settlement_id}`} className="inline-block mr-1 align-middle">
                        {isExpanded
                          ? <ChevronDown size={12} className="text-emerald-700 inline" />
                          : <ChevronRight size={12} className="text-ink-soft inline" />}
                      </span>
                    )}
                    {m.transfer_date}
                  </td>
                  <td className="py-2 font-mono text-ink whitespace-nowrap">{m.settlement_id}</td>
                  <td className="py-2 text-right whitespace-nowrap font-mono">RM {(m.net_total || 0).toFixed(2)}</td>
                  <td className="py-2 whitespace-nowrap">
                    <span
                      data-testid={`grab-match-status-${m.settlement_id}`}
                      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] border ${badgeCls}`}
                    >
                      {badgeLbl}
                    </span>
                  </td>
                  <td className="py-2 whitespace-nowrap text-ink">
                    {m.pos_date || "—"}
                    {isExpandable && (
                      <span className="ml-1 text-ink-soft text-[10px]">· {m.pos_orders.length} orders</span>
                    )}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap font-mono">{m.pos_gross != null ? `RM ${m.pos_gross.toFixed(2)}` : "—"}</td>
                  <td className="py-2 text-right whitespace-nowrap text-ink-soft">{m.commission_pct != null ? `${m.commission_pct}%` : "—"}</td>
                  <td className="py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {m.status === "unmatched" && (
                      <div className="flex flex-col gap-1">
                        {(m.candidates || []).length === 0 ? (
                          <span className="text-ink-soft text-[11px]">No candidates within ±3 days</span>
                        ) : (
                          (m.candidates || []).map((c) => (
                            <button
                              key={c.pos_date}
                              type="button"
                              data-testid={`grab-match-link-${m.settlement_id}-${c.pos_date}`}
                              onClick={() => linkTransfer(m.settlement_id, c.pos_date)}
                              className="text-left text-[11px] hover:bg-emerald-100/50 px-1.5 py-0.5 rounded-sm border border-transparent hover:border-emerald-700/25"
                              title={`Score ${c.score} · ${c.day_diff}d off · commission ${c.commission_pct}%`}
                            >
                              <span className="font-mono text-ink">{c.pos_date}</span>
                              <span className="text-ink-soft"> · {c.order_count} orders</span>
                              <span className="text-ink-soft"> · RM {c.pos_gross.toFixed(2)}</span>
                              <span className="text-ink-soft"> ({c.commission_pct}%)</span>
                            </button>
                          ))
                        )}
                        <button
                          type="button"
                          data-testid={`grab-match-nomatch-${m.settlement_id}`}
                          onClick={() => markNoMatch(m.settlement_id)}
                          className="text-left text-[11px] text-ink-soft hover:text-ink px-1.5 py-0.5"
                        >
                          Mark as no-POS-match…
                        </button>
                      </div>
                    )}
                    {(m.status === "manual" || m.status === "no_match") && (
                      <button
                        type="button"
                        data-testid={`grab-match-clear-${m.settlement_id}`}
                        onClick={() => clearOverride(m.settlement_id)}
                        className="text-[11px] text-ink-soft hover:text-ink underline"
                      >
                        Clear override
                      </button>
                    )}
                    {m.status === "matched" && (
                      <span className="text-[11px] text-emerald-700 flex items-center gap-1">
                        Auto-matched
                        <button
                          type="button"
                          data-testid={`grab-match-delete-${m.settlement_id}`}
                          onClick={() => removeTransfer(m.id)}
                          className="p-0.5 rounded hover:bg-red-50 text-red-600 ml-1"
                          title="Delete this transfer"
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-emerald-50/40" data-testid={`grab-match-detail-${m.settlement_id}`}>
                    <td colSpan="8" className="px-4 py-3">
                      <p className="overline text-emerald-800 mb-2">
                        GEBME orders on {m.pos_date} · {m.pos_orders.length} orders · gross RM {m.pos_gross.toFixed(2)}
                      </p>
                      <table className="w-full text-[11px]">
                        <thead className="text-left text-ink-soft">
                          <tr>
                            <th className="py-1 overline">Order #</th>
                            <th className="py-1 overline">Invoice #</th>
                            <th className="py-1 overline">Order Type</th>
                            <th className="py-1 overline">Payment Mode</th>
                            <th className="py-1 overline">Status</th>
                            <th className="py-1 overline text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.pos_orders.map((o) => (
                            <tr key={o.order_id} className="border-t border-ink/5" data-testid={`grab-match-detail-order-${o.order_id}`}>
                              <td className="py-1 font-mono text-ink whitespace-nowrap">{o.order_no || "—"}</td>
                              <td className="py-1 font-mono text-ink-soft whitespace-nowrap">{o.invoice_no || "—"}</td>
                              <td className="py-1 text-ink whitespace-nowrap">{o.order_type || "—"}</td>
                              <td className="py-1 text-ink-soft whitespace-nowrap">{o.payment_mode}</td>
                              <td className="py-1 text-ink-soft whitespace-nowrap">{o.order_status || "—"}</td>
                              <td className="py-1 text-right whitespace-nowrap font-mono text-ink">RM {Number(o.total_price || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-emerald-700/20">
                            <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Sum</td>
                            <td className="py-1 text-right font-mono font-semibold text-ink">RM {m.pos_gross.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Grab Net Paid</td>
                            <td className="py-1 text-right font-mono text-ink">RM {(m.net_total || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Commission</td>
                            <td className="py-1 text-right font-mono text-red-700">RM {(m.pos_gross - (m.net_total || 0)).toFixed(2)} <span className="text-ink-soft">({m.commission_pct}%)</span></td>
                          </tr>
                        </tfoot>
                      </table>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ---------------- Touch 'n Go daily settlement section ----------------
// One MDSDO_P_YYYYMMDD_<extmid>.xlsx = one bank credit day. Idempotent by
// Report Reference. Every file carries a header "Settlement Date" (CIMB
// credit date, T+1) and inline transaction rows dated the day before
// (POS sale date). The recon shows settlement vs POS TNG gross vs CIMB
// AUTOPAY CR credit per settlement.
function TngSettlementSection({ month, setMonth, ohBump = 0 }) {
  const [settlements, setSettlements] = React.useState([]);
  const [recon, setRecon] = React.useState(null);
  const [reconLoading, setReconLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [expandedId, setExpandedId] = React.useState(null);
  const [expandedRows, setExpandedRows] = React.useState([]);
  const [expandedLoading, setExpandedLoading] = React.useState(false);
  const fileRef = React.useRef(null);

  const loadSettlements = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/mgmt/bank/tng-settlements?month=${month}`);
      setSettlements(data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }, [month]);

  const loadRecon = React.useCallback(async () => {
    setReconLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/tng-recon?month=${month}`);
      setRecon(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setReconLoading(false);
    }
  }, [month]);

  React.useEffect(() => { loadSettlements(); }, [loadSettlements]);
  React.useEffect(() => { loadRecon(); }, [loadRecon]);
  // Refresh when GEBME Order History is (re)imported so POS gross updates.
  React.useEffect(() => {
    if (ohBump === 0) return;
    loadRecon();
  }, [ohBump, loadRecon]);

  const uploadXlsx = async (f) => {
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: parsed } = await api.post(
        "/mgmt/bank/tng-settlements/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!window.confirm(
        `Import Touch 'n Go daily settlement?\n\n` +
        `File: ${f.name}\n` +
        `Report Reference: ${parsed.report_reference}\n` +
        `Settlement Date (bank credit): ${parsed.settlement_date}\n` +
        `Transaction Date (sales): ${parsed.transaction_date}\n` +
        `Transactions: ${parsed.row_count}\n` +
        `Total settled: RM ${Number(parsed.total_settlement_amount).toFixed(2)}\n\n` +
        `Existing rows with the same Report Reference will be replaced.`
      )) { setUploading(false); return; }
      await api.post(
        "/mgmt/bank/tng-settlements/commit",
        { ...parsed, filename: f.name },
      );
      // Bump month to the file's settlement month if different
      const sMonth = (parsed.settlement_date || "").slice(0, 7);
      if (sMonth && sMonth !== month) setMonth(sMonth);
      setErr(`Imported · ${parsed.row_count} transactions · RM ${Number(parsed.total_settlement_amount).toFixed(2)}`);
      await loadSettlements();
      await loadRecon();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const removeSettlement = async (id) => {
    if (!window.confirm("Delete this TNG settlement report? The recon will refresh.")) return;
    try {
      await api.delete(`/mgmt/bank/tng-settlements/${id}`);
      if (expandedId === id) { setExpandedId(null); setExpandedRows([]); }
      await loadSettlements();
      await loadRecon();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null); setExpandedRows([]); return;
    }
    setExpandedId(id);
    setExpandedRows([]);
    setExpandedLoading(true);
    try {
      const { data } = await api.get(`/mgmt/bank/tng-settlements/${id}`);
      setExpandedRows(data?.rows || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setExpandedLoading(false);
    }
  };

  const totals = recon?.totals || {};
  const rows = recon?.rows || [];

  return (
    <section
      className="mt-10 rounded-sm border border-blue-700/25 bg-blue-50/30 p-6 space-y-4 shadow-sm"
      data-testid="tng-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-ink/10 pb-4">
        <div>
          <p className="overline text-blue-700">Section 6 · Touch 'n Go Daily Settlement</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            TNG daily report ↔ GEBME POS ↔ CIMB bank credit
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Drop the daily <strong className="text-ink">MDSDO_P_YYYYMMDD_*.xlsx</strong> report Touch 'n Go emails —
            we import every DUITNOW_QR transaction (reference, timestamp, amount) and reconcile the day's
            total against GEBME <em>offline-tng</em> orders and the matching CIMB AUTOPAY CR line from
            UTMB TNG DIGITAL. Idempotent by Report Reference.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline text-ink-soft">Month</label>
          <input
            type="month"
            data-testid="tng-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls + " w-40"}
          />
        </div>
      </div>

      {err && (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 px-3 py-2 rounded-sm" data-testid="tng-err">
          {err}
        </p>
      )}

      <div
        data-testid="tng-drop-zone"
        className="border-2 border-dashed border-blue-600/40 rounded-sm p-8 text-center bg-white/40 hover:bg-white/70 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) uploadXlsx(f);
        }}
      >
        <input
          ref={fileRef}
          data-testid="tng-file-input"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => uploadXlsx(e.target.files?.[0])}
        />
        <Upload className="mx-auto text-blue-700" size={32} />
        <p className="mt-3 text-ink">
          <strong>Drop Touch 'n Go daily settlement XLSX here</strong> or click to browse
        </p>
        <p className="text-ink-soft text-xs mt-1">
          Idempotent by Report Reference · 100% offline parser
        </p>
        {uploading && (
          <p className="mt-3 inline-flex items-center gap-2 text-blue-700">
            <Loader2 className="animate-spin" size={16} />
            Importing…
          </p>
        )}
      </div>

      {/* Totals */}
      {recon && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Settlements"
            value={String(settlements.length)}
            sub={`${totals.txn_count || 0} transactions · RM ${(totals.settlement_amount || 0).toFixed(2)} settled`}
          />
          <Stat
            label="POS TNG Gross"
            value={`RM ${(totals.pos_gross || 0).toFixed(2)}`}
            sub="GEBME offline-tng orders on txn date"
          />
          <Stat
            label="CIMB TNG Credit"
            value={`RM ${(totals.bank_credit || 0).toFixed(2)}`}
            sub="AUTOPAY CR from UTMB TNG DIGITAL"
          />
          <Stat
            label="POS vs Settle Variance"
            value={`RM ${(totals.variance_pos_vs_settle || 0).toFixed(2)}`}
            tone={Math.abs(totals.variance_pos_vs_settle || 0) < 0.5 ? "pos" : "neg"}
            sub={`Bank vs Settle: RM ${(totals.variance_bank_vs_settle || 0).toFixed(2)}`}
          />
        </div>
      )}

      {/* Daily recon table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tng-recon-table">
          <thead className="text-left text-ink-soft border-b border-ink/10">
            <tr>
              <th className="py-2 overline">Settle Date</th>
              <th className="py-2 overline">Txn Date</th>
              <th className="py-2 overline">Report Reference</th>
              <th className="py-2 overline text-right">Txns</th>
              <th className="py-2 overline text-right">Settled</th>
              <th className="py-2 overline text-right">POS Gross</th>
              <th className="py-2 overline text-right">POS Δ</th>
              <th className="py-2 overline text-right">Bank Credit</th>
              <th className="py-2 overline text-right">Bank Δ</th>
              <th className="py-2 overline"> </th>
            </tr>
          </thead>
          <tbody>
            {reconLoading && (
              <tr><td colSpan="10" className="py-4 text-center text-ink-soft">
                <Loader2 className="inline animate-spin mr-2" size={12} />Loading…
              </td></tr>
            )}
            {!reconLoading && rows.length === 0 && (
              <tr><td colSpan="10" className="py-6 text-center text-ink-soft" data-testid="tng-recon-empty">
                No TNG settlements for <strong className="text-ink">{month}</strong> yet.
                Upload a daily MDSDO_P_*.xlsx above to populate this table.
              </td></tr>
            )}
            {!reconLoading && rows.map((r) => {
              const isExpanded = expandedId === r.id;
              const posDelta  = r.variance_pos_vs_settle;
              const bankDelta = r.variance_bank_vs_settle;
              const posCls  = Math.abs(posDelta)  < 0.5 ? "text-emerald-700" : "text-amber-700";
              const bankCls = Math.abs(bankDelta) < 0.5 ? "text-emerald-700" : "text-amber-700";
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-t border-ink/5 align-top cursor-pointer hover:bg-blue-50/40"
                    data-testid={`tng-recon-row-${r.id}`}
                    onClick={() => toggleExpand(r.id)}
                  >
                    <td className="py-2 text-ink whitespace-nowrap">
                      <span className="inline-block mr-1 align-middle">
                        {isExpanded
                          ? <ChevronDown size={12} className="text-blue-700 inline" />
                          : <ChevronRight size={12} className="text-ink-soft inline" />}
                      </span>
                      {r.settlement_date}
                    </td>
                    <td className="py-2 text-ink-soft whitespace-nowrap">{r.transaction_date || "—"}</td>
                    <td className="py-2 font-mono text-ink whitespace-nowrap">{r.report_reference}</td>
                    <td className="py-2 text-right whitespace-nowrap text-ink-soft">{r.row_count}</td>
                    <td className="py-2 text-right whitespace-nowrap font-mono text-ink">RM {Number(r.settlement_amount || 0).toFixed(2)}</td>
                    <td className="py-2 text-right whitespace-nowrap font-mono text-ink-soft">
                      {r.pos_gross ? `RM ${r.pos_gross.toFixed(2)}` : "—"}
                      {r.pos_orders ? <span className="block text-[10px] text-ink-soft">{r.pos_orders} orders</span> : null}
                    </td>
                    <td className={`py-2 text-right whitespace-nowrap font-mono ${posCls}`}>RM {Number(posDelta || 0).toFixed(2)}</td>
                    <td className="py-2 text-right whitespace-nowrap font-mono text-ink-soft">
                      {r.bank_credit ? `RM ${r.bank_credit.toFixed(2)}` : "—"}
                    </td>
                    <td className={`py-2 text-right whitespace-nowrap font-mono ${bankCls}`}>RM {Number(bankDelta || 0).toFixed(2)}</td>
                    <td className="py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        data-testid={`tng-recon-delete-${r.id}`}
                        onClick={() => removeSettlement(r.id)}
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        title="Delete this TNG settlement"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50/40" data-testid={`tng-recon-detail-${r.id}`}>
                      <td colSpan="10" className="px-4 py-3">
                        <p className="overline text-blue-800 mb-2">
                          {expandedLoading
                            ? "Loading transactions…"
                            : `${expandedRows.length} transactions on ${r.transaction_date} · settled RM ${r.settlement_amount.toFixed(2)}`}
                        </p>
                        {!expandedLoading && expandedRows.length > 0 && (
                          <table className="w-full text-[11px]">
                            <thead className="text-left text-ink-soft">
                              <tr>
                                <th className="py-1 overline">#</th>
                                <th className="py-1 overline">Time</th>
                                <th className="py-1 overline">Type</th>
                                <th className="py-1 overline">Txn Reference</th>
                                <th className="py-1 overline">Settlement Mode</th>
                                <th className="py-1 overline text-right">Amount</th>
                                <th className="py-1 overline text-right">Commission</th>
                                <th className="py-1 overline text-right">Settled</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedRows.map((tx, i) => (
                                <tr key={tx.txn_ref || i} className="border-t border-ink/5" data-testid={`tng-txn-${tx.txn_ref}`}>
                                  <td className="py-1 text-ink-soft">{tx.no?.replace(".0", "") || i + 1}</td>
                                  <td className="py-1 text-ink-soft whitespace-nowrap">{(tx.txn_datetime || "").slice(-8)}</td>
                                  <td className="py-1 text-ink whitespace-nowrap">{tx.txn_type}</td>
                                  <td className="py-1 font-mono text-ink whitespace-nowrap">{tx.txn_ref}</td>
                                  <td className="py-1 text-ink-soft">{tx.settlement_mode}</td>
                                  <td className="py-1 text-right font-mono text-ink">RM {Number(tx.txn_amount || 0).toFixed(2)}</td>
                                  <td className="py-1 text-right font-mono text-ink-soft">RM {Number((tx.commission_amount || 0) + (tx.commission_gst || 0)).toFixed(2)}</td>
                                  <td className="py-1 text-right font-mono text-ink">RM {Number(tx.settlement_amount || 0).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t border-blue-700/20">
                                <td colSpan="5" className="py-1 text-right text-ink-soft uppercase tracking-[0.14em] text-[10px]">Total</td>
                                <td className="py-1 text-right font-mono font-semibold text-ink">
                                  RM {expandedRows.reduce((s, t) => s + Number(t.txn_amount || 0), 0).toFixed(2)}
                                </td>
                                <td className="py-1 text-right font-mono text-ink-soft">
                                  RM {expandedRows.reduce((s, t) => s + Number((t.commission_amount || 0) + (t.commission_gst || 0)), 0).toFixed(2)}
                                </td>
                                <td className="py-1 text-right font-mono font-semibold text-ink">
                                  RM {expandedRows.reduce((s, t) => s + Number(t.settlement_amount || 0), 0).toFixed(2)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ---------------- Historical uploads strip ----------------
// Sits above the CIMB reconciliation and lets the admin click any past
// month to instantly load its full recon (all four sections refetch).
// The backend `/mgmt/bank/upload-index` returns a per-month rollup with
// counts for CIMB · Merchant · Foodpanda · Grab · Order History.
function HistoricalUploadsStrip({ month, setMonth }) {
  const [months, setMonths] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mgmt/bank/upload-index");
      setMonths(data?.months || []);
    } catch (e) {
      // silent — this strip is informational only
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  // Refresh when month changes so newly-committed uploads show up
  React.useEffect(() => { load(); }, [month, load]);

  if (loading && months.length === 0) return null;
  if (!loading && months.length === 0) return null;

  const fmtMonth = (m) => {
    const [y, mo] = m.split("-");
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${names[parseInt(mo, 10) - 1]} ${y}`;
  };

  return (
    <div
      className="rounded-sm border border-ink/10 bg-cream/40 p-4"
      data-testid="historical-uploads"
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <p className="overline text-ink-soft">Historical uploads · click to revisit</p>
          <p className="text-ink text-xs mt-1">
            Every statement, PAYADV, Grab CSV and Order History is stored per-month.
            Click a chip to reload all four Bank-Recon sections for that period.
          </p>
        </div>
        <button
          type="button"
          data-testid="historical-refresh"
          onClick={load}
          className="p-1.5 rounded hover:bg-white/80 text-ink-soft"
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {months.map((m) => {
          const isActive = m.month === month;
          const kinds = [
            m.cimb          && ["CIMB", "border-ink/25 text-ink"],
            m.merchant      && ["Merchant", "border-emerald-700/40 text-emerald-800"],
            m.foodpanda     && [`Foodpanda ${m.foodpanda}`, "border-pink-600/40 text-pink-800"],
            m.grab          && [`Grab ${m.grab}`, "border-emerald-600/40 text-emerald-800"],
            m.order_history && [`POS ${m.order_history.toLocaleString()}`, "border-ink-soft/30 text-ink-soft"],
          ].filter(Boolean);
          return (
            <button
              key={m.month}
              type="button"
              data-testid={`historical-month-${m.month}`}
              onClick={() => setMonth(m.month)}
              className={`text-left px-3 py-2 rounded-sm border transition-colors ${
                isActive
                  ? "border-matcha-dark bg-white shadow-sm"
                  : "border-ink/15 bg-white/60 hover:bg-white hover:border-ink/30"
              }`}
            >
              <div className={`font-display text-sm ${isActive ? "text-matcha-dark" : "text-ink"}`}>
                {fmtMonth(m.month)}
                {isActive && <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-matcha-dark">viewing</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {kinds.length === 0 && <span className="text-[10px] text-ink-soft">no uploads</span>}
                {kinds.map(([label, cls], i) => (
                  <span
                    key={i}
                    className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.14em] border ${cls} bg-white/70`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

